<template>
    <div class="dashboard-body-futuristic">
        <main class="futuristic-container">
            <!-- Sidebar -->
            <AppSidebar :activeItem="sidebarActive" />

            <!-- Main Card -->
            <section class="dashboard-card-futuristic dashboard-card-scrollable">
                <div class="bd-wrapper">

                    <!-- Loading -->
                    <div v-if="loading" class="bd-loading">
                        <div class="bd-spinner"></div>
                        <p>Loading bid details…</p>
                    </div>

                    <template v-else>
                        <!-- Top Back button -->
                        <div class="bd-top-bar">
                            <button class="bd-back" @click="goBack">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2.5">
                                    <line x1="19" y1="12" x2="5" y2="12" />
                                    <polyline points="12 19 5 12 12 5" />
                                </svg>
                                Back
                            </button>
                        </div>

                        <!-- HERO: Bid identity + status -->
                        <div class="bd-hero">
                            <div class="bd-hero-left">
                                <span class="bd-status-badge hero-left-badge" :title="statusHint">
                                    {{ statusLabel }}<span class="sr-only">. {{ statusHint }}</span>
                                </span>
                                <h1 v-if="!editingName" class="bd-name bd-name-editable"
                                    :title="'Double-click to rename'"
                                    @dblclick="startEditName">{{ stripExt(bid.project_name) }}</h1>
                                <div v-else class="bd-name-edit">
                                    <input ref="nameInput" v-model="editNameValue" type="text"
                                        class="bd-name-input" :disabled="savingName"
                                        @keyup.enter="saveName" @keyup.esc="cancelEditName"
                                        placeholder="Project name" />
                                    <button class="bd-name-btn save" @click="saveName" :disabled="savingName"
                                        title="Save" aria-label="Save Name">
                                        <svg v-if="!savingName" width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                            stroke-linejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        <svg v-else class="bd-start-spin" width="16" height="16" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                        </svg>
                                    </button>
                                    <button class="bd-name-btn cancel" @click="cancelEditName" :disabled="savingName"
                                        title="Cancel" aria-label="Cancel Rename">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                            stroke-linejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                </div>
                                <p class="bd-subname">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    {{ stripExt(bid.pdf_name) || '—' }}
                                </p>
                            </div>
                            <div class="bd-hero-right">
                                <div v-if="mode != 'post'" class="bd-countdown" :class="deadlineUrgency">
                                    <span class="cd-num">{{ daysLeftLabel }}</span>
                                    <span class="cd-cap">to submission</span>
                                </div>
                                <router-link v-if="bid.tender_id" class="bd-ppt-btn"
                                    :to="{ name: 'TenderDetail', params: { id: bid.tender_id } }">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <circle cx="11" cy="11" r="8" />
                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    <span>View Tender</span>
                                </router-link>
                                <button class="bd-ppt-btn" @click="downloadPpt" :disabled="downloadingPpt"
                                    title="Download bid overview presentation">
                                    <svg v-if="!downloadingPpt" width="15" height="15" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                                        stroke-linejoin="round">
                                        <rect x="2" y="3" width="20" height="14" rx="2" />
                                        <line x1="8" y1="21" x2="16" y2="21" />
                                        <line x1="12" y1="17" x2="12" y2="21" />
                                    </svg>
                                    <svg v-else class="bd-start-spin" width="15" height="15" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                    </svg>
                                    <span>{{ downloadingPpt ? 'Downloading…' : 'Download PPT' }}</span>
                                </button>
                                <!-- Status changes (admin only): what's offered depends on the
                                     current status — see utils/bidStatus.js. -->
                                <div v-if="isCompanyAdmin && statusActions.length" class="bd-status-actions"
                                    role="group" aria-label="Change bid status">
                                    <button v-for="action in statusActions" :key="action.to" type="button"
                                        class="bd-status-btn" :class="statusActionClass(action)"
                                        :disabled="!!changingStatus" @click="changeStatus(action)">
                                        <svg v-if="changingStatus === action.to" class="bd-start-spin" width="15"
                                            height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="3" stroke-linecap="round" aria-hidden="true">
                                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                        </svg>
                                        <span>{{ action.label }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Addendum trail (admin): shows the tender was amended & what refreshed -->
                        <div v-if="isCompanyAdmin && addenda.length" class="bd-addenda-trail">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            </svg>
                            <span>
                                {{ addenda.length }} addendum{{ addenda.length > 1 ? 'a' : '' }} applied ·
                                last updated {{ formatAddendumDate(addenda[addenda.length - 1].uploaded_at) }}
                                (refreshed: {{ (addenda[addenda.length - 1].affects || []).join(', ') || '—' }})
                            </span>
                        </div>

                        <!-- KEY METRIC TILES -->
                        <div class="bd-metrics">
                            <div class="metric-tile blue">
                                <div class="metric-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <line x1="12" y1="1" x2="12" y2="23" />
                                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                                    </svg>
                                </div>
                                <div class="metric-body">
                                    <label>Estimated Value</label>
                                    <h3>{{ formattedAmount }}</h3>
                                </div>
                            </div>
                            <div v-if="!hideBidDates" class="metric-tile rose">
                                <div class="metric-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                    </svg>
                                </div>
                                <div class="metric-body">
                                    <label>Last Submission Date</label>
                                    <h3>{{ formatDate(bid.last_submission_date) }}</h3>
                                </div>
                            </div>
                            <div v-if="!hideBidDates" class="metric-tile green">
                                <div class="metric-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                </div>
                                <div class="metric-body">
                                    <label>Bid Opening Date</label>
                                    <h3>{{ formatDate(bid.bid_opening_date) }}</h3>
                                </div>
                            </div>
                            <div class="metric-tile" :class="categoryClass">
                                <div class="metric-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <path
                                            d="M20 7h-9M14 17H5M17 17a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM7 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                                    </svg>
                                </div>
                                <div class="metric-body">
                                    <label>Category</label>
                                    <h3>{{ bid.bid_category || '—' }}</h3>
                                </div>
                            </div>
                        </div>

                        <!-- AI run still going (or failed): sections below may be partly empty. -->
                        <div v-if="analysisState !== 'ready'" class="bd-analysis-note" :class="analysisState"
                            role="status">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span v-if="analysing">
                                <strong>Analysis in progress.</strong> Sections fill in as each step finishes.
                                Refresh in a few minutes to see the latest.
                            </span>
                            <span v-else>
                                <strong>Analysis didn’t finish.</strong> Some sections may be empty. An admin can
                                retry it from the Admin Panel.
                            </span>
                        </div>

                        <!-- SECTION TABS: one group of portlets at a time. Synced with ?tab=. -->
                        <div class="bd-tabs" role="tablist" aria-label="Bid sections" @keydown="onTabKeydown">
                            <button v-for="t in tabs" :key="t.key" :id="'bd-tab-' + t.key" type="button"
                                role="tab" class="bd-tab" :class="{ active: activeTab === t.key }"
                                :aria-selected="activeTab === t.key ? 'true' : 'false'"
                                :aria-controls="'bd-panel-' + t.key" :tabindex="activeTab === t.key ? 0 : -1"
                                @click="setTab(t.key)">
                                {{ t.label }}
                            </button>
                        </div>

                        <div v-show="activeTab === 'overview'" id="bd-panel-overview" class="bd-tabpanel"
                            role="tabpanel" aria-labelledby="bd-tab-overview" tabindex="0">
                            <div class="bd-detail-block">
                                <!-- Full-width detail card -->
                                <div class="bd-detail-card">
                                    <div class="bd-card-head">
                                        <h3>Bid Information</h3>
                                    </div>
                                    <!-- The metric tiles above are the glance; this is the record,
                                         so the four headline facts repeat here deliberately —
                                         a half-empty card reads as broken, and the tiles are
                                         scrolled away by the time anyone reads this far.
                                         Rows whose value is missing are skipped rather than
                                         showing a bare "—"; Status always renders. -->
                                    <ul class="bd-field-list">
                                        <li v-if="nameOfWorkDisplay" class="fl-block">
                                            <span class="f-label">Name of Work</span>
                                            <p class="f-value f-prose">{{ nameOfWorkDisplay }}</p>
                                        </li>
                                        <li v-if="bid.client_name">
                                            <span class="f-label">Client</span>
                                            <span class="f-value">{{ bid.client_name }}</span>
                                        </li>
                                        <li v-if="bid.bid_topic">
                                            <span class="f-label">Bid Topic</span>
                                            <span class="f-value">{{ bid.bid_topic }}</span>
                                        </li>
                                        <li>
                                            <span class="f-label">Estimated Value</span>
                                            <span class="f-value f-num strong">{{ formattedAmount }}</span>
                                        </li>
                                        <li v-if="bid.bid_category">
                                            <span class="f-label">Category</span>
                                            <span class="f-value f-category" :class="categoryClass">
                                                <span class="cat-dot"></span>{{ bid.bid_category }}
                                            </span>
                                        </li>
                                        <li v-if="!hideBidDates">
                                            <span class="f-label">Last Submission Date</span>
                                            <span class="f-value f-num">{{ formatDate(bid.last_submission_date) }}</span>
                                        </li>
                                        <li v-if="!hideBidDates">
                                            <span class="f-label">Bid Opening Date</span>
                                            <span class="f-value f-num">{{ formatDate(bid.bid_opening_date) }}</span>
                                        </li>
                                        <li v-if="bid.tender_id">
                                            <span class="f-label">Source Tender</span>
                                            <span class="f-value">
                                                <router-link class="f-link"
                                                    :to="{ name: 'TenderDetail', params: { id: bid.tender_id } }">
                                                    View tender listing
                                                </router-link>
                                            </span>
                                        </li>
                                        <li v-if="bid.pdf_name">
                                            <span class="f-label">Source File</span>
                                            <span class="f-value f-mono">{{ bid.pdf_name }}</span>
                                        </li>
                                        <li v-if="bid.created_date">
                                            <span class="f-label">Uploaded On</span>
                                            <span class="f-value f-num">{{ formatDate(bid.created_date) }}</span>
                                        </li>
                                        <li>
                                            <span class="f-label">Status</span>
                                            <span class="f-value f-status">
                                                <span class="bd-status-badge sm" :class="statusClass">{{ statusLabel
                                                    }}</span>
                                                <span class="f-hint">{{ statusHint }}</span>
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <!-- AI RISK ASSESSMENT -->
                            <div class="bd-risk-panel">
                                <div class="risk-head">
                                    <div class="risk-head-icon">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                            <line x1="12" y1="9" x2="12" y2="13" />
                                            <line x1="12" y1="17" x2="12.01" y2="17" />
                                        </svg>
                                    </div>
                                    <div class="risk-head-text">
                                        <h3>AI Risk Assessment</h3>
                                        <p>Automated contractual &amp; commercial risk scoring from the tender document.</p>
                                    </div>
                                    <span class="risk-ai-chip">AI</span>
                                </div>

                                <!-- Loading -->
                                <div v-if="riskLoading" class="risk-state">
                                    <div class="risk-spinner"></div>
                                    <span>Loading risk assessment…</span>
                                </div>

                                <!-- Not yet computed -->
                                <div v-else-if="!riskComputed" class="risk-state empty">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                    </svg>
                                    Risk assessment is being generated. Check back shortly.
                                </div>

                                <!-- Risk grid -->
                                <template v-else>
                                    <!-- Severity triage: counts double as filter chips -->
                                    <div class="risk-triage">
                                        <span class="risk-triage-label" id="risk-triage-label">Severity</span>
                                        <div class="risk-chips" role="group" aria-labelledby="risk-triage-label">
                                            <button type="button" class="risk-chip chip-all"
                                                :class="{ active: riskFilter === 'all' }"
                                                :aria-pressed="riskFilter === 'all' ? 'true' : 'false'"
                                                @click="setRiskFilter('all')">
                                                All <span class="chip-count">{{ riskCounts.total }}</span>
                                            </button>
                                            <button type="button" class="risk-chip chip-high"
                                                :class="{ active: riskFilter === 'high' }"
                                                :aria-pressed="riskFilter === 'high' ? 'true' : 'false'"
                                                @click="setRiskFilter('high')">
                                                <span class="chip-count">{{ riskCounts.high }}</span> High
                                            </button>
                                            <button type="button" class="risk-chip chip-medium"
                                                :class="{ active: riskFilter === 'medium' }"
                                                :aria-pressed="riskFilter === 'medium' ? 'true' : 'false'"
                                                @click="setRiskFilter('medium')">
                                                <span class="chip-count">{{ riskCounts.medium }}</span> Medium
                                            </button>
                                            <button type="button" class="risk-chip chip-low"
                                                :class="{ active: riskFilter === 'low' }"
                                                :aria-pressed="riskFilter === 'low' ? 'true' : 'false'"
                                                @click="setRiskFilter('low')">
                                                <span class="chip-count">{{ riskCounts.low }}</span> Low
                                            </button>
                                        </div>
                                    </div>

                                    <div class="risk-grid">
                                        <div v-for="row in visibleRiskRows" :key="row.key" class="risk-card"
                                            :class="'risk-' + (row.level || 'none')">
                                            <div class="risk-card-top">
                                                <span class="risk-name">{{ row.label }}</span>
                                                <span class="risk-badge" :class="'lvl-' + (row.level || 'none')">
                                                    {{ row.level ? row.level.toUpperCase() : 'N/A' }}
                                                </span>
                                            </div>
                                            <p class="risk-detail">{{ row.detail || '—' }}</p>
                                        </div>
                                    </div>

                                    <div v-if="!visibleRiskRows.length" class="risk-state empty">
                                        No risks at this severity.
                                    </div>

                                    <!-- Low/unknown risks are collapsed by default so HIGH + MEDIUM lead. -->
                                    <div v-if="riskFilter === 'all' && lowRiskRows.length" class="risk-lowtoggle">
                                        <button type="button" class="risk-low-btn"
                                            :aria-expanded="showLowRisks ? 'true' : 'false'"
                                            @click="showLowRisks = !showLowRisks">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <polyline :points="showLowRisks ? '18 15 12 9 6 15' : '6 9 12 15 18 9'" />
                                            </svg>
                                            {{ showLowRisks
                                                ? ('Hide ' + lowRiskLabel)
                                                : ('Show ' + lowRiskRows.length + ' ' + lowRiskLabel) }}
                                        </button>
                                    </div>
                                </template>
                            </div>
                        </div>

                        <div v-show="activeTab === 'analysis'" id="bd-panel-analysis" class="bd-tabpanel"
                            role="tabpanel" aria-labelledby="bd-tab-analysis" tabindex="0">
                            <div class="bd-portlets-grid">
                                <!-- Portlet: Bid Summary -->
                                <div class="portlet summary" :class="analysisCardClass('summary')">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="16" y1="13" x2="8" y2="13" />
                                                <line x1="16" y1="17" x2="8" y2="17" />
                                                <polyline points="10 9 9 9 8 9" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Bid Summary</h4>
                                            <p>Clause-wise summary of the tender’s key provisions.</p>
                                        </div>
                                    <span class="portlet-state" :class="'ps-' + analysisCardStates.summary">
                                        <span class="ps-dot" aria-hidden="true"></span>
                                        <span class="bd-sr-only">Status: </span><span class="ps-text">{{ analysisStateText('summary') }}</span>
                                    </span>
                                    </div>
                                    <!-- Summary preview (first few heads) -->
                                    <div class="summary-preview" v-if="analysisCardStates.summary !== 'empty'">
                                        <div v-if="summaryDataList && summaryDataList.length" class="summary-items">
                                            <div v-for="summaryData in summaryDataList" :key="summaryData.id"
                                                class="summary-item">
                                                <div class="si-head">
                                                    <span class="si-bullet"></span>
                                                    {{ summaryData.head_name }}
                                                </div>
                                                <p class="si-desc">{{ truncateWords(summaryData.brief_description, 100)
                                                    }}</p>
                                            </div>
                                        </div>
                                        <div v-else class="summary-empty">
                                            <svg v-if="analysisCardStates.summary === 'processing'" class="spin ps-spin" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                            </svg>
                                            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                            {{ analysing ? 'Analysis in progress. The summary appears when it’s ready.' : 'No summary generated yet.' }}
                                        </div>
                                    </div>

                                    <p class="portlet-quiet" v-if="analysisCardStates.summary === 'empty'">No summary generated yet.</p>
                                    <div class="portlet-actions">
                                        <button class="pb-btn detail" @click="viewSummary" :disabled="isAnalysisProcessing('summary')">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            Detail View
                                        </button>
                                        <button class="pb-btn download" @click="downloadSummary"
                                            :disabled="downloadingSummary || isAnalysisProcessing('summary')">
                                            <svg v-if="!downloadingSummary" width="16" height="16" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" stroke-width="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="7 10 12 15 17 10" />
                                                <line x1="12" y1="15" x2="12" y2="3" />
                                            </svg>
                                            <svg v-else class="spin" width="16" height="16" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" stroke-width="2">
                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                            </svg>
                                            {{ downloadingSummary ? 'Downloading…' : 'Download' }}
                                        </button>
                                    </div>
                                </div>

                                <!-- Portlet: Risk Register -->
                                <div class="portlet riskregister" :class="analysisCardClass('riskregister')">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                                <line x1="12" y1="9" x2="12" y2="13" />
                                                <line x1="12" y1="17" x2="12.01" y2="17" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Risk Register</h4>
                                            <p>Phase-wise risk review — occurrence, impact, severity &amp; mitigation.</p>
                                        </div>
                                    <span class="portlet-state" :class="'ps-' + analysisCardStates.riskregister">
                                        <span class="ps-dot" aria-hidden="true"></span>
                                        <span class="bd-sr-only">Status: </span><span class="ps-text">{{ analysisStateText('riskregister') }}</span>
                                    </span>
                                    </div>
                                    <!-- Risk register preview (first few risks) -->
                                    <div class="summary-preview" v-if="analysisCardStates.riskregister !== 'empty'">
                                        <div v-if="riskRegisterList && riskRegisterList.length" class="summary-items">
                                            <div v-for="risk in riskRegisterList" :key="risk.id"
                                                class="summary-item">
                                                <div class="si-head">
                                                    <span class="si-bullet"></span>
                                                    {{ risk.subject_of_risk }} - {{ risk.item_description }}
                                                </div>
                                                <p class="si-desc">Chances of occurrence: {{ truncateWords(risk.chances_of_occurrence, 100) }}</p>
                                                <p class="si-desc">Impact of risk: {{ truncateWords(risk.impact_of_risk, 100) }}</p>
                                                <p class="si-desc">Severity of risk:
                                                    <span v-if="risk.severity_of_risk" class="sev-badge"
                                                        :class="severityClass(risk.severity_of_risk)">{{ risk.severity_of_risk }}</span>
                                                    <span v-else>—</span>
                                                </p>
                                            </div>
                                        </div>
                                        <div v-else class="summary-empty">
                                            <svg v-if="analysisCardStates.riskregister === 'processing'" class="spin ps-spin" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                            </svg>
                                            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                            {{ analysing ? 'Analysis in progress. The risk register appears when it’s ready.' : 'No risk register generated yet.' }}
                                        </div>
                                    </div>

                                    <p class="portlet-quiet" v-if="analysisCardStates.riskregister === 'empty'">No risk register generated yet.</p>
                                    <div class="portlet-actions">
                                        <button class="pb-btn detail" @click="viewRiskRegister" :disabled="isAnalysisProcessing('riskregister')">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            Detail View
                                        </button>
                                        <button class="pb-btn download" @click="downloadRiskRegister"
                                            :disabled="downloadingRiskRegister || isAnalysisProcessing('riskregister')">
                                            <svg v-if="!downloadingRiskRegister" width="16" height="16" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" stroke-width="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="7 10 12 15 17 10" />
                                                <line x1="12" y1="15" x2="12" y2="3" />
                                            </svg>
                                            <svg v-else class="spin" width="16" height="16" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" stroke-width="2">
                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                            </svg>
                                            {{ downloadingRiskRegister ? 'Downloading…' : 'Download' }}
                                        </button>
                                    </div>
                                </div>

                                <!-- Portlet: Acceptance Criteria -->
                                <div class="portlet acceptance" :class="analysisCardClass('acceptance')">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                                <polyline points="22 4 12 14.01 9 11.01" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Acceptance Criteria</h4>
                                            <p>Terms &amp; conditions that must be accepted to qualify for the bid.</p>
                                        </div>
                                    <span class="portlet-state" :class="'ps-' + analysisCardStates.acceptance">
                                        <span class="ps-dot" aria-hidden="true"></span>
                                        <span class="bd-sr-only">Status: </span><span class="ps-text">{{ analysisStateText('acceptance') }}</span>
                                    </span>
                                    </div>
                                    <!-- Criteria preview (first few terms) -->
                                    <div class="summary-preview" v-if="analysisCardStates.acceptance !== 'empty'">
                                        <div v-if="acceptanceTableDataList && acceptanceTableDataList.length"
                                            class="summary-items">
                                            <div v-for="(acceptanceTableData, idx) in acceptanceTableDataList"
                                                :key="acceptanceTableData.id" class="summary-item green-accent">
                                                <div class="si-head">
                                                    <span class="si-bullet green"></span>
                                                    Criterion {{ idx + 1 }}
                                                    <span v-if="acceptanceTableData.clause_no" class="si-clause">
                                                        Clause {{ acceptanceTableData.clause_no }}
                                                    </span>
                                                </div>
                                                <p class="si-desc">{{ truncateWords(acceptanceTableData.term_condition,
                                                    100) }}</p>
                                            </div>
                                        </div>
                                        <div v-else class="summary-empty">
                                            <svg v-if="analysisCardStates.acceptance === 'processing'" class="spin ps-spin" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                            </svg>
                                            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                            {{ analysing ? 'Analysis in progress. Acceptance criteria appear when they’re ready.' : 'No acceptance criteria found.' }}
                                        </div>
                                    </div>

                                    <p class="portlet-quiet" v-if="analysisCardStates.acceptance === 'empty'">No acceptance criteria found.</p>
                                    <div class="portlet-actions">
                                        <button class="pb-btn detail" @click="viewAcceptance" :disabled="isAnalysisProcessing('acceptance')">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            Detail View
                                        </button>
                                        <button class="pb-btn download" @click="downloadAcceptance"
                                            :disabled="downloadingAcceptance || isAnalysisProcessing('acceptance')">
                                            <svg v-if="!downloadingAcceptance" width="16" height="16"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="7 10 12 15 17 10" />
                                                <line x1="12" y1="15" x2="12" y2="3" />
                                            </svg>
                                            <svg v-else class="spin" width="16" height="16" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" stroke-width="2">
                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                            </svg>
                                            {{ downloadingAcceptance ? 'Downloading…' : 'Download' }}
                                        </button>
                                    </div>
                                </div>

                                <!-- Portlet: Eligibility Criteria -->
                                <div class="portlet eligibility" :class="analysisCardClass('eligibility')">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                                <polyline points="9 12 11 14 15 10" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Eligibility Criteria</h4>
                                            <p>Who can bid: the qualifying requirements extracted from the tender.</p>
                                        </div>
                                    <span class="portlet-state" :class="'ps-' + analysisCardStates.eligibility">
                                        <span class="ps-dot" aria-hidden="true"></span>
                                        <span class="bd-sr-only">Status: </span><span class="ps-text">{{ analysisStateText('eligibility') }}</span>
                                    </span>
                                    </div>
                                    <!-- Eligibility preview (first few requirements) -->
                                    <div class="summary-preview" v-if="analysisCardStates.eligibility !== 'empty'">
                                        <div v-if="eligibilityPreview.length" class="summary-items">
                                            <div v-for="item in eligibilityPreview" :key="item.id"
                                                class="summary-item">
                                                <div class="si-head">
                                                    <span class="si-bullet"></span>
                                                    {{ item.group }}
                                                    <span v-if="item.clause" class="si-clause">
                                                        Clause {{ item.clause }}
                                                    </span>
                                                </div>
                                                <p class="si-desc">{{ truncateWords(item.text, 100) }}</p>
                                            </div>
                                        </div>
                                        <div v-else class="summary-empty">
                                            <svg v-if="analysisCardStates.eligibility === 'processing'" class="spin ps-spin" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                            </svg>
                                            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                            {{ analysing ? 'Analysis in progress. Eligibility criteria appear when they’re ready.' : 'No eligibility criteria found.' }}
                                        </div>
                                    </div>

                                    <p class="portlet-quiet" v-if="analysisCardStates.eligibility === 'empty'">No eligibility criteria found.</p>
                                    <div class="portlet-actions">
                                        <button class="pb-btn detail" @click="viewEligibility" :disabled="isAnalysisProcessing('eligibility')">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            Detail View
                                        </button>
                                    </div>
                                </div>

                                <!-- Portlet: Obligations (Contractor vs Client, with due dates) -->
                                <div class="portlet obligations" :class="analysisCardClass('obligations')">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M9 11l3 3L22 4" />
                                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Obligations</h4>
                                            <p>Contractor &amp; Client obligations with due dates, extracted from the tender.</p>
                                        </div>
                                    <span class="portlet-state" :class="'ps-' + analysisCardStates.obligations">
                                        <span class="ps-dot" aria-hidden="true"></span>
                                        <span class="bd-sr-only">Status: </span><span class="ps-text">{{ analysisStateText('obligations') }}</span>
                                    </span>
                                    </div>
                                    <div class="summary-preview" v-if="analysisCardStates.obligations !== 'empty'">
                                        <div v-if="obligationSummary.total" class="summary-items">
                                            <div class="summary-item">
                                                <div class="si-head">
                                                    <span class="si-bullet"></span>
                                                    Contractor obligations: {{ obligationSummary.contractor_count }}
                                                </div>
                                            </div>
                                            <div class="summary-item">
                                                <div class="si-head">
                                                    <span class="si-bullet"></span>
                                                    Client obligations: {{ obligationSummary.client_count }}
                                                </div>
                                            </div>
                                        </div>
                                        <div v-else class="summary-empty">
                                            <svg v-if="analysisCardStates.obligations === 'processing'" class="spin ps-spin" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                            </svg>
                                            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                            {{ analysing ? 'Analysis in progress. Obligations appear when they’re ready.' : 'No obligations extracted yet.' }}
                                        </div>
                                    </div>
                                    <p class="portlet-quiet" v-if="analysisCardStates.obligations === 'empty'">No obligations extracted yet.</p>
                                    <div class="portlet-actions">
                                        <button class="pb-btn detail" @click="viewObligations" :disabled="isAnalysisProcessing('obligations')">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            Detail View
                                        </button>
                                    </div>
                                </div>

                                <!-- Portlet: Features Checklist — switched off
                                     for now (config/features.js) -->
                                <div v-if="checklistOn" class="portlet checklist">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                                stroke-linejoin="round">
                                                <path d="M9 11l3 3L22 4" />
                                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Features Checklist</h4>
                                            <p>Check off your pre-bid work &amp; record remarks for each item.</p>
                                        </div>
                                    </div>
                                    <!-- Checklist progress preview -->
                                    <div class="summary-preview">
                                        <div v-if="checklistTotal" class="cl-mini-progress">
                                            <div class="cl-mini-bar-wrap">
                                                <div class="cl-mini-bar"
                                                    :style="{ width: checklistPercent + '%' }"></div>
                                            </div>
                                            <span class="cl-mini-label">
                                                {{ checklistCompleted }}/{{ checklistTotal }} done
                                                ({{ checklistPercent }}%)
                                            </span>
                                        </div>
                                        <div v-else class="summary-empty">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                            {{ analysing ? 'Analysis in progress. Checklist items appear when they’re ready.' : 'No checklist items yet.' }}
                                        </div>
                                    </div>

                                    <div class="portlet-actions">
                                        <button class="pb-btn download" @click="viewChecklist">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M9 11l3 3L22 4" />
                                                <path
                                                    d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                            </svg>
                                            Open Checklist
                                        </button>
                                    </div>
                                </div>

                                <!-- Portlet: Deliverables (post-bid / project-execution stage) -->
                                <div v-if="postBidOn" class="portlet deliverables">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M9 11l3 3L22 4" />
                                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Deliverables</h4>
                                            <p>Contractor deliverables &amp; milestones with deadline reminders.</p>
                                        </div>
                                    </div>
                                    <div class="summary-preview">
                                        <div v-if="deliverablesTotal" class="cl-mini-progress">
                                            <div class="cl-mini-bar-wrap">
                                                <div class="cl-mini-bar teal"
                                                    :style="{ width: deliverablesPercent + '%' }"></div>
                                            </div>
                                            <span class="cl-mini-label">
                                                {{ deliverablesCompleted }}/{{ deliverablesTotal }} done
                                                ({{ deliverablesPercent }}%)
                                                <template v-if="deliverablesOverdue">
                                                    · <strong class="cl-overdue">{{ deliverablesOverdue }} overdue</strong>
                                                </template>
                                            </span>
                                        </div>
                                        <div v-else class="summary-empty">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                            {{ analysing ? 'Analysis in progress. Deliverables appear when they’re ready.' : 'No deliverables extracted yet.' }}
                                        </div>
                                    </div>

                                    <div class="portlet-actions">
                                        <button class="pb-btn download deliverables-btn"
                                            @click="viewDeliverables">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M9 11l3 3L22 4" />
                                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                            </svg>
                                            Open Deliverables
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="postBidOn" v-show="activeTab === 'project'" id="bd-panel-project" class="bd-tabpanel"
                            role="tabpanel" aria-labelledby="bd-tab-project" tabindex="0">
                            <div class="bd-portlets-grid">
                                <!-- Email Records for this project -->
                                <div class="portlet email">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path
                                                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                <polyline points="22,6 12,13 2,6" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Email Records</h4>
                                            <p>Emails linked to this project</p>
                                        </div>
                                    </div>

                                    <!-- Records preview box (matches Bid Summary / Acceptance Criteria) -->
                                    <div class="summary-preview">
                                        <div v-if="emailsLoading" class="summary-empty">
                                            Loading…
                                        </div>
                                        <ul v-else-if="emailRecords.length" class="bd-email-list">
                                            <li v-for="email in emailRecords" :key="email.id" class="bd-email-item">
                                                <div class="bd-email-info">
                                                    <span class="bd-email-subject" :title="email.email_subject">{{
                                                        email.email_subject || '(No subject)' }}</span>
                                                    <span class="bd-email-meta">{{ email.email_from }} · {{
                                                        formatDate(email.email_date) }}</span>
                                                </div>
                                                <span class="bd-email-type">{{ email.email_type }}</span>
                                                <div class="bd-email-actions">
                                                    <button class="bd-email-btn" @click="viewEmailDetail(email.id)"
                                                        title="View details" aria-label="View Email Details">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                            stroke="currentColor" stroke-width="2">
                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                            <circle cx="12" cy="12" r="3" />
                                                        </svg>
                                                    </button>
                                                    <button v-if="email.has_file" class="bd-email-btn"
                                                        @click="downloadMailPdf(email.id)"
                                                        :disabled="downloadingEmailIds.includes(email.id)" title="Download"
                                                        aria-label="Download Email Attachment">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                            stroke="currentColor" stroke-width="2">
                                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                            <polyline points="7 10 12 15 17 10" />
                                                            <line x1="12" y1="15" x2="12" y2="3" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </li>
                                        </ul>
                                        <div v-else class="summary-empty">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                            </svg>
                                            No email records yet.
                                        </div>
                                    </div>

                                    <div class="portlet-actions">
                                        <!-- Detail views live in the Admin Panel, which viewers can't open. -->
                                        <button v-if="isCompanyAdmin" class="pb-btn detail" @click="goToEmailRecords">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            Detail View
                                        </button>
                                        <span v-else class="admin-only-note">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                            </svg>
                                            Admins only
                                        </span>
                                    </div>
                                </div>

                                <!-- Contract Documents for this project -->
                                <div class="portlet docs">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Contract Documents</h4>
                                            <p>Files linked to this project</p>
                                        </div>
                                    </div>

                                    <div class="summary-preview docs-tree">
                                        <div class="folder-block">
                                            <button type="button" class="folder-header"
                                                :aria-expanded="openFolders.contract ? 'true' : 'false'"
                                                @click="openFolders.contract = !openFolders.contract">
                                                <svg class="folder-icon" width="15" height="15" viewBox="0 0 24 24"
                                                    fill="currentColor" aria-hidden="true">
                                                    <path
                                                        d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                                </svg>
                                                <span class="folder-name">Contract Documents</span>
                                                <span class="folder-count">{{ contractDocs.length }} files</span>
                                                <svg class="folder-chevron" :class="{ open: openFolders.contract }"
                                                    width="13" height="13" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                                    <polyline points="9 18 15 12 9 6" />
                                                </svg>
                                            </button>
                                            <transition name="expand">
                                                <div v-if="openFolders.contract" class="file-list">
                                                    <div v-if="contractDocsLoading" class="file-row file-row-note">
                                                        Loading…</div>
                                                    <div v-else-if="!contractDocs.length" class="file-row file-row-note">
                                                        No documents uploaded yet</div>
                                                    <div v-else v-for="file in contractDocs.slice(0, 3)" :key="file.id"
                                                        class="file-row">
                                                        <svg class="file-icon pdf" width="13" height="13" viewBox="0 0 24 24"
                                                            fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                            <path
                                                                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                            <polyline points="14 2 14 8 20 8" />
                                                        </svg>
                                                        <span class="file-name" :title="file.name">{{ file.name }}</span>
                                                        <span class="file-size">{{ formatDate(file.date) }}</span>
                                                        <button class="file-download-btn"
                                                            @click.stop="downloadMailPdf(file.id)"
                                                            :disabled="downloadingEmailIds.includes(file.id)"
                                                            title="Download" :aria-label="'Download ' + file.name">
                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                                                stroke="currentColor" stroke-width="2.5">
                                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                                <polyline points="7,10 12,15 17,10" />
                                                                <line x1="12" y1="15" x2="12" y2="3" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                    <template v-if="contractDocs.length > 3">
                                                        <button v-if="isCompanyAdmin" type="button" class="file-row file-row-more"
                                                            @click.stop="goToContractDocs()">
                                                            + {{ contractDocs.length - 3 }} more files
                                                        </button>
                                                        <div v-else class="file-row file-row-note">
                                                            + {{ contractDocs.length - 3 }} more files
                                                        </div>
                                                    </template>
                                                </div>
                                            </transition>
                                        </div>

                                        <!-- Obligation Documents folder -->
                                        <div class="folder-block">
                                            <button type="button" class="folder-header"
                                                :aria-expanded="openFolders.obligation ? 'true' : 'false'"
                                                @click="openFolders.obligation = !openFolders.obligation">
                                                <svg class="folder-icon" width="15" height="15" viewBox="0 0 24 24"
                                                    fill="currentColor" aria-hidden="true">
                                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                                </svg>
                                                <span class="folder-name">Obligation Documents</span>
                                                <span class="folder-count">{{ obligationDocs.length }} files</span>
                                                <svg class="folder-chevron" :class="{ open: openFolders.obligation }"
                                                    width="13" height="13" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                                    <polyline points="9 18 15 12 9 6" />
                                                </svg>
                                            </button>
                                            <transition name="expand">
                                                <div v-if="openFolders.obligation" class="file-list">
                                                    <div v-if="obligationDocsLoading" class="file-row file-row-note">Loading…</div>
                                                    <div v-else-if="!obligationDocs.length" class="file-row file-row-note">No obligation letters generated yet</div>
                                                    <div v-else v-for="file in obligationDocs.slice(0, 3)" :key="file.id" class="file-row">
                                                        <svg class="file-icon letter" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                                        <span class="file-name" :title="file.name">{{ file.name }}</span>
                                                        <span class="file-size">{{ formatDate(file.date) }}</span>
                                                        <button class="file-download-btn" @click.stop="downloadObligationPdf(file.id)" :disabled="downloadingEmailIds.includes(file.id)" title="Download" :aria-label="'Download ' + file.name">
                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                                        </button>
                                                    </div>
                                                    <template v-if="obligationDocs.length > 3">
                                                        <button v-if="isCompanyAdmin" type="button" class="file-row file-row-more" @click.stop="goToObligations()">
                                                            + {{ obligationDocs.length - 3 }} more files
                                                        </button>
                                                        <div v-else class="file-row file-row-note">
                                                            + {{ obligationDocs.length - 3 }} more files
                                                        </div>
                                                    </template>
                                                </div>
                                            </transition>
                                        </div>

                                    </div>

                                    <div class="portlet-actions">
                                        <template v-if="isCompanyAdmin">
                                            <button class="pb-btn detail" @click="goToContractDocs">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2">
                                                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                                Detail View
                                            </button>
                                            <button class="pb-btn download" @click="goToUploadPage">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="17 8 12 3 7 8" />
                                                    <line x1="12" y1="3" x2="12" y2="15" />
                                                </svg>
                                                Upload Documents
                                            </button>
                                        </template>
                                        <span v-else class="admin-only-note">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                            </svg>
                                            Admins only
                                        </span>
                                    </div>
                                </div>

                                <!-- Compensation Claims: penalty / performance-failure records -->
                                <div class="portlet docs claims">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <path d="M12 18l4-4-4-4" />
                                                <path d="M8 14h8" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Compensation Claims</h4>
                                            <p>Compensation records &amp; mails</p>
                                        </div>
                                    </div>

                                    <div class="summary-preview">
                                        <div v-if="obligationDocsLoading" class="summary-empty">
                                            Loading…
                                        </div>
                                        <ul v-else-if="obligationDocs.length" class="bd-email-list">
                                            <li v-for="doc in obligationDocs.slice(0, 3)" :key="doc.id"
                                                class="bd-email-item">
                                                <div class="bd-email-info">
                                                    <span class="bd-email-subject" :title="doc.name">{{ doc.name }}</span>
                                                    <span class="bd-email-meta">{{ formatDate(doc.date) }}</span>
                                                </div>
                                                <div class="bd-email-actions">
                                                    <button class="bd-email-btn" @click="downloadObligationPdf(doc.id)"
                                                        :disabled="downloadingEmailIds.includes(doc.id)" title="Download"
                                                        :aria-label="'Download ' + doc.name">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                            stroke="currentColor" stroke-width="2">
                                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                            <polyline points="7 10 12 15 17 10" />
                                                            <line x1="12" y1="15" x2="12" y2="3" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </li>
                                            <li v-if="obligationDocs.length > 3" class="bd-email-item bd-email-more">
                                                <button v-if="isCompanyAdmin" type="button" class="bd-more-link"
                                                    @click="goToObligations">
                                                    + {{ obligationDocs.length - 3 }} more files
                                                </button>
                                                <span v-else>+ {{ obligationDocs.length - 3 }} more files</span>
                                            </li>
                                        </ul>
                                        <div v-else class="summary-empty">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                            </svg>
                                            No compensation claims yet.
                                        </div>
                                    </div>

                                    <div class="portlet-actions">
                                        <template v-if="isCompanyAdmin">
                                            <button class="pb-btn detail" @click="goToObligations">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2">
                                                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                                Detail View
                                            </button>
                                            <button class="pb-btn download" @click="generateObligationMail">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2">
                                                    <line x1="12" y1="5" x2="12" y2="19" />
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                </svg>
                                                Generate Mail &amp; Record
                                            </button>
                                        </template>
                                        <span v-else class="admin-only-note">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                            </svg>
                                            Admins only
                                        </span>
                                    </div>
                                </div>

                                <!-- Contact List -->
                                <div class="portlet contact">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                <circle cx="9" cy="7" r="4" />
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Client Contacts</h4>
                                            <p>Project contacts directory</p>
                                        </div>
                                    </div>

                                    <div class="summary-preview">
                                        <div v-if="contactsLoading" class="summary-empty">Loading…</div>
                                        <template v-else-if="contacts.length">
                                            <div v-for="c in contacts.slice(0, 3)" :key="c.id" class="bd-contact-item">
                                                <div class="bd-contact-avatar">
                                                    {{ (c.contact_name || '?')[0].toUpperCase() }}
                                                </div>
                                                <div class="bd-contact-info">
                                                    <span class="bd-contact-name">{{ c.contact_name }}</span>
                                                    <span class="bd-contact-meta">{{ c.contact_email }}</span>
                                                    <span v-if="c.contact_mobile" class="bd-contact-meta">{{ c.contact_mobile }}</span>
                                                </div>
                                            </div>
                                            <button v-if="contacts.length > 3" type="button" class="bd-contact-more"
                                                @click="goToContactList">
                                                +{{ contacts.length - 3 }} more contacts
                                            </button>
                                        </template>
                                        <div v-else class="summary-empty">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                <circle cx="9" cy="7" r="4" />
                                            </svg>
                                            No contacts added yet.
                                        </div>
                                    </div>

                                    <div class="portlet-actions">
                                        <button class="pb-btn detail" @click="goToContactList">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            Detail View
                                        </button>
                                        <button v-if="isCompanyAdmin" class="pb-btn download" @click="goToContactList">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <line x1="12" y1="5" x2="12" y2="19" />
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                            </svg>
                                            Add Contact
                                        </button>
                                    </div>
                                </div>

                                <!-- Email Configuration (admin only) -->
                                <div v-if="isCompanyAdmin" class="portlet contact liaison">
                                    <div class="portlet-top">
                                        <div class="portlet-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                <polyline points="22,6 12,13 2,6" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4>Client Liaison/Point Of Contact</h4>
                                            <p>Mailbox used to pull bid correspondence</p>
                                        </div>
                                    </div>

                                    <div class="summary-preview">
                                        <div v-if="emailConfig.loading" class="summary-empty">Loading…</div>
                                        <template v-else-if="emailConfig.configured">
                                            <div class="bd-contact-item">
                                                <div class="bd-contact-avatar" aria-hidden="true">@</div>
                                                <div class="bd-contact-info">
                                                    <span class="bd-contact-name">{{ emailConfig.summary.contact_person_email }}</span>
                                                    <span class="bd-contact-meta">IMAP: {{ emailConfig.summary.imap_server }}</span>
                                                </div>
                                            </div>
                                        </template>
                                        <div v-else class="summary-empty">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                <polyline points="22,6 12,13 2,6" />
                                            </svg>
                                            No email configuration set yet.
                                        </div>
                                    </div>

                                    <div class="portlet-actions">
                                        <button class="pb-btn download" @click="openEmailConfig">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2">
                                                <line x1="12" y1="5" x2="12" y2="19" />
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                            </svg>
                                            {{ emailConfig.configured ? 'Edit Configuration' : 'Add Configuration' }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Bottom-left Back button -->
                        <div class="bd-footer">
                            <button class="bd-back" @click="goBack">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2.5">
                                    <line x1="19" y1="12" x2="5" y2="12" />
                                    <polyline points="12 19 5 12 12 5" />
                                </svg>
                                Back
                            </button>
                        </div>
                    </template>
                </div>
            </section>
        </main>

        <!-- Email Config Modal (admin-only) -->
        <div v-if="emailConfig.show" class="ec-modal-overlay" @click.self="closeEmailConfig">
            <div class="ec-modal">
                <div class="ec-modal-header">
                    <h2>Email Configuration</h2>
                    <button class="ec-close" @click="closeEmailConfig" aria-label="Close">&times;</button>
                </div>
                <p class="ec-subtitle" :title="nameOfWork">
                    {{ nameOfWork }}
                </p>

                <div v-if="emailConfig.loading" class="ec-loading">Loading configuration…</div>

                <form v-else @submit.prevent="saveEmailConfig">
                    <div class="ec-field">
                        <label>Contact Person Email <span class="ec-req">*</span></label>
                        <input type="email" v-model.trim="emailConfig.form.contact_person_email"
                            placeholder="e.g. bids@yourcompany.com" required />
                    </div>

                    <div class="ec-field">
                        <label>Email App Password <span class="ec-req">*</span></label>
                        <div class="ec-password-row">
                            <input :type="emailConfig.showPassword ? 'text' : 'password'"
                                v-model="emailConfig.form.email_password"
                                placeholder="App password / mailbox password" required />
                            <button type="button" class="ec-toggle-pass"
                                @click="emailConfig.showPassword = !emailConfig.showPassword">
                                {{ emailConfig.showPassword ? 'Hide' : 'Show' }}
                            </button>
                        </div>
                    </div>

                    <div class="ec-field">
                        <label>IMAP Server <span class="ec-req">*</span></label>
                        <input type="text" v-model.trim="emailConfig.form.imap_server"
                            placeholder="e.g. imap.gmail.com" required />
                    </div>

                    <p v-if="emailConfig.error" class="ec-error">{{ emailConfig.error }}</p>

                    <div class="ec-actions">
                        <button type="button" class="ec-btn-cancel" @click="closeEmailConfig" :disabled="emailConfig.saving">
                            Cancel
                        </button>
                        <button type="submit" class="ec-btn-save" :disabled="emailConfig.saving">
                            {{ emailConfig.saving ? 'Saving...' : 'Save' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Email detail (Project tab › Email Records › eye button) -->
        <div v-if="emailDetail.show" class="ec-modal-overlay" @click.self="closeEmailDetail">
            <div class="ec-modal" role="dialog" aria-modal="true" aria-labelledby="bd-email-detail-title"
                @keydown.esc="closeEmailDetail">
                <div class="ec-modal-header">
                    <h2 id="bd-email-detail-title">Email Details</h2>
                    <button ref="emailDetailClose" class="ec-close" @click="closeEmailDetail"
                        aria-label="Close">&times;</button>
                </div>
                <div v-if="emailDetail.loading" class="ec-loading">Loading email…</div>
                <template v-else-if="emailDetail.data">
                    <dl class="bd-email-detail">
                        <dt>Subject</dt>
                        <dd>{{ emailDetail.data.email_subject || '(No subject)' }}</dd>
                        <dt>From</dt>
                        <dd>{{ emailDetail.data.email_from || '—' }}</dd>
                        <dt>Date</dt>
                        <dd>{{ formatDate(emailDetail.data.email_date) }}</dd>
                        <dt>Type</dt>
                        <dd>{{ emailDetail.data.email_type || '—' }}</dd>
                    </dl>
                    <div class="bd-email-body">{{ emailDetail.data.email_body || 'No message body.' }}</div>
                </template>
                <div class="ec-actions">
                    <button type="button" class="ec-btn-cancel" @click="closeEmailDetail">Close</button>
                </div>
            </div>
        </div>

        <!-- Addendum / Corrigendum upload modal (admin) -->
        <div v-if="addendumModal.show" class="ec-modal-overlay" @click.self="closeAddendumModal">
            <div class="ec-modal adm-modal">
                <div class="ec-modal-header">
                    <h2>Upload Addendum / Corrigendum</h2>
                    <button class="ec-close" @click="closeAddendumModal" aria-label="Close">&times;</button>
                </div>
                <p class="ec-subtitle" :title="nameOfWork">{{ nameOfWork }}</p>

                <p class="adm-help">
                    Add an addendum or corrigendum PDF for this tender. Only the sections it changes
                    (eligibility criteria, risk, deliverables, dates, etc.) are re-processed — the whole
                    tender is not re-ingested. Scanned PDFs are handled automatically.
                </p>

                <!-- Drop zone -->
                <div class="adm-drop" :class="{ 'is-drag': addendumModal.dragOver }"
                    @click="triggerAddendumUpload"
                    @dragover.prevent="addendumModal.dragOver = true"
                    @dragleave.prevent="addendumModal.dragOver = false"
                    @drop.prevent="onAddendumDrop">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <p><strong>Click to choose</strong> or drag &amp; drop PDF files here</p>
                    <span class="adm-hint">PDF only · multiple files allowed</span>
                    <input ref="addendumInput" type="file" accept=".pdf" multiple
                        style="display:none" @change="onAddendumSelected" />
                </div>

                <!-- Selected files -->
                <ul v-if="addendumModal.files.length" class="adm-file-list">
                    <li v-for="(f, i) in addendumModal.files" :key="i">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span class="adm-file-name">{{ f.name }}</span>
                        <span class="adm-file-size">{{ formatFileSize(f.size) }}</span>
                        <button type="button" class="adm-file-remove" @click="removeAddendumFile(i)"
                            :disabled="uploadingAddendum" title="Remove">&times;</button>
                    </li>
                </ul>

                <p v-if="addendumModal.error" class="ec-error">{{ addendumModal.error }}</p>

                <div class="ec-actions">
                    <button type="button" class="ec-btn-cancel" @click="closeAddendumModal" :disabled="uploadingAddendum">
                        Cancel
                    </button>
                    <button type="button" class="ec-btn-save" @click="submitAddendum"
                        :disabled="uploadingAddendum || !addendumModal.files.length">
                        {{ uploadingAddendum ? 'Uploading…' : 'Upload & Process' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import AppSidebar from './Sidebar.vue';
import { isAdmin as userIsAdmin } from '@/utils/auth';
import { notify } from '@/utils/notify';
import { confirmAction } from '@/utils/confirm';
import { STATUS_LABEL, STATUS_HINT, effectiveStatus, actionsFor, actionsForStatus, isProjectStatus } from '@/utils/bidStatus';
import { POST_BID_ENABLED, CHECKLIST_ENABLED } from '@/config/features';

// Section tabs below the hero. "project" exists only once the bid is won.
// A bid not marked DONE after this long counts as ready (legacy rows have no
// summary_status, and a stuck job shouldn't read "in progress" forever).
const ANALYSIS_WINDOW_MS = 2 * 60 * 60 * 1000;
const TAB_LABELS = { overview: 'Overview', analysis: 'Analysis', project: 'Project' };

export default {
    name: 'BidDetails',
    components: { AppSidebar },
    props: {
        getAccessToken: Function,
    },
    data() {
        return {
            loading: true,
            // Target status of the change in flight (drives the button spinner).
            changingStatus: null,
            // Inline project-name editing (double-click header)
            editingName: false,
            editNameValue: '',
            savingName: false,
            downloadingSummary: false,
            downloadingAcceptance: false,
            downloadingRiskRegister: false,
            downloadingPpt: false,
            uploadingAddendum: false,
            addenda: [],
            addendumModal: { show: false, files: [], dragOver: false, error: '' },
            emailRecords: [],
            emailsLoading: false,
            downloadingEmailIds: [],
            contractDocs: [],
            contractDocsLoading: false,
            contacts: [],
            contactsLoading: false,
            obligationDocs: [],
            obligationDocsLoading: false,
            openFolders: { contract: false, obligation: false },
            emailDetail: { show: false, loading: false, data: null },
            bid: {
                pdf_name: '',
                name_of_work: '',
                client_name: '',
                bid_topic: '',
                estimated_cost: '',
                bid_category: '',
                last_submission_date: null,
                bid_opening_date: null,
                // Empty until the bid row loads, so ?status= can stand in meanwhile.
                bid_status: '',
            },
            summaryDataList: [],
            riskRegisterList: [],
            obligationSummary: { total: 0, contractor_count: 0, client_count: 0 },
            acceptanceTableDataList: [],
            eligibilityTechnicalList: [],
            eligibilityFinancialList: [],
            checklistItems: [],
            deliverables: [],
            riskAssessment: null,
            riskLoading: true,
            // Severity triage for the AI Risk Assessment grid.
            // 'all' | 'high' | 'medium' | 'low' — presentation only, never filters data away
            // permanently (the chips and the disclosure below the grid always lead back to All).
            riskFilter: 'all',
            // LOW (and unparsed) risks are demoted behind a disclosure so HIGH/MEDIUM lead.
            showLowRisks: false,
            // Admin-only IMAP email-fetch config. `configured`/`summary` drive the
            // portlet preview; the modal (show/loading/saving/form) handles add+edit.
            emailConfig: {
                configured: false,
                summary: { contact_person_email: '', imap_server: '' },
                show: false,
                loading: false,
                saving: false,
                showPassword: false,
                error: '',
                form: {
                    contact_person_email: '',
                    email_password: '',
                    imap_server: ''
                }
            },
        };
    },
    computed: {
        pdfId() {
            return this.$route.params.pdf_id;
        },
        isCompanyAdmin() {
            // Same rule as everywhere else (admin or superadmin).
            return userIsAdmin();
        },
        statusFromQuery() {
            return String(this.$route.query.status || '').trim().toUpperCase();
        },
        // Stored status: the server's row wins; ?status= only covers the moment
        // before it loads (and a bid the fallback lookup couldn't find).
        storedStatus() {
            return String(this.bid.bid_status || this.statusFromQuery || 'NEW').toUpperCase();
        },
        // What the person sees: a NEW bid past its deadline reads as EXPIRED.
        currentStatus() {
            if (this.bid.bid_status) return effectiveStatus(this.bid);
            return this.statusFromQuery || 'NEW';
        },
        nameOfWork() {
            return this.bid.name_of_work || this.stripExt(this.bid.pdf_name) || 'Untitled Bid';
        },
        // Bid Information row: the tender's own wording, verbatim. Acronyms like
        // NHAI/IREPS/EPC/BOQ are domain vocabulary, so the casing is never
        // transformed — readability is handled typographically in .f-prose.
        // Blank (so the row is skipped) when there is no real name.
        nameOfWorkDisplay() {
            return (this.bid.name_of_work || this.stripExt(this.bid.pdf_name) || '').trim();
        },
        statusLabel() {
            return STATUS_LABEL[this.currentStatus] || this.currentStatus;
        },
        statusHint() {
            return STATUS_HINT[this.currentStatus] || '';
        },
        statusClass() {
            return 'st-' + this.currentStatus.toLowerCase();
        },
        statusActions() {
            if (this.bid.bid_status) return actionsFor(this.bid);
            return actionsForStatus(this.currentStatus);
        },
        // 'ready' | 'analysing' | 'failed' — mirrors BidListing.analysisState().
        analysisState() {
            const s = String(this.bid.summary_status || '').toUpperCase();
            if (s === 'DONE') return 'ready';
            if (s === 'FAILED') return 'failed';
            // Server-side flag (DB clock); the created_date check below is the
            // fallback for an older backend.
            if (typeof this.bid.is_processing === 'boolean') return this.bid.is_processing ? 'analysing' : 'ready';
            const created = this.bid.created_date ? new Date(this.bid.created_date).getTime() : NaN;
            if (isNaN(created) || Date.now() - created > ANALYSIS_WINDOW_MS) return 'ready';
            return 'analysing';
        },
        analysing() {
            return this.analysisState === 'analysing';
        },
        // Post-bid portlets show only for won/completed bids AND when the
        // post-bid system is switched on.
        postBidOn() {
            return POST_BID_ENABLED && this.mode === 'post';
        },
        checklistOn() {
            return CHECKLIST_ENABLED;
        },
        tabs() {
            // Checklist lives inside Analysis as a portlet — a tab of its own
            // held a single card and a button that navigated away.
            const keys = ['overview', 'analysis'];
            // Project tab = post-bid; hidden while post-bid is off (config/features.js).
            if (this.postBidOn) keys.push('project');
            return keys.map(key => ({ key, label: TAB_LABELS[key] }));
        },
        activeTab() {
            const t = String(this.$route.query.tab || '');
            return this.tabs.some(x => x.key === t) ? t : 'overview';
        },
        categoryClass() {
            const c = (this.bid.bid_category || '').toLowerCase();
            if (c.includes('rail')) return 'cat-railway';
            if (c.includes('high')) return 'cat-highway';
            if (c.includes('metro')) return 'cat-metro';
            return 'cat-default';
        },
        formattedAmount() {
            const raw = this.bid.estimated_cost;
            if (raw === null || raw === undefined || raw === '') return '—';
            const num = Number(String(raw).replace(/[^0-9.]/g, ''));
            // A tender's estimated cost is never genuinely zero — a 0 means the
            // extraction found no figure, so say that rather than "₹0".
            if (num === 0) return 'Not stated';
            if (!isFinite(num)) return String(raw);
            return new Intl.NumberFormat('en-IN', {
                style: 'currency', currency: 'INR', maximumFractionDigits: 0,
            }).format(num);
        },
        daysLeft() {
            if (!this.bid.last_submission_date) return null;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const due = new Date(this.bid.last_submission_date);
            due.setHours(0, 0, 0, 0);
            return Math.round((due - today) / (1000 * 60 * 60 * 24));
        },
        daysLeftLabel() {
            // Once a bid is won/completed the submission window is over, so the
            // header shows "Closed" (not "—") even though the date is no longer
            // tracked on the post-bid row.
            if (this.mode === 'post') return 'Closed';
            const d = this.daysLeft;
            if (d === null) return '—';
            if (d < 0) return 'Closed';
            if (d === 0) return 'Today';
            return d + (d === 1 ? ' day' : ' days');
        },
        deadlineUrgency() {
            if (this.mode === 'post') return 'closed';
            const d = this.daysLeft;
            if (d === null || d < 0) return 'closed';
            if (d <= 3) return 'urgent';
            if (d <= 10) return 'soon';
            return 'normal';
        },
        sidebarActive() {
            // Pre-bid statuses surface under "files" (Bids); won under "active", completed under "completed".
            const s = this.storedStatus;
            if (!POST_BID_ENABLED) return 'files';
            if (s === 'COMPLETED') return 'completed';
            if (s === 'WIN') return 'active';
            return 'files';
        },
        mode() {
            // Single source of truth for pre-bid vs post-bid: the loaded bid row,
            // or ?status= until it arrives.
            // pre  → NEW (Open, or Expired once the deadline passes)
            // post → WIN / COMPLETED (project execution stage, mail automation)
            return isProjectStatus(this.storedStatus) ? 'post' : 'pre';
        },
        hideBidDates() {
            // Submission/opening dates are irrelevant once a bid is won or completed.
            return this.mode === 'post';
        },
        checklistTotal() {
            return this.checklistItems.length;
        },
        checklistCompleted() {
            return this.checklistItems.filter(i => !!i.completed).length;
        },
        checklistPercent() {
            if (!this.checklistTotal) return 0;
            return Math.round((this.checklistCompleted / this.checklistTotal) * 100);
        },
        deliverablesTotal() {
            return this.deliverables.length;
        },
        deliverablesCompleted() {
            return this.deliverables.filter(i => !!i.completed).length;
        },
        deliverablesPercent() {
            if (!this.deliverablesTotal) return 0;
            return Math.round((this.deliverablesCompleted / this.deliverablesTotal) * 100);
        },
        deliverablesOverdue() {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return this.deliverables.filter(i => {
                if (i.completed || !i.due_date) return false;
                const due = new Date(i.due_date + 'T00:00:00');
                return due < today;
            }).length;
        },
        riskRows() {
            // Map the stored risk keys to display rows, in the same order as the
            // backend RISK_KEYS. Each value is a "<Level> - <justification>"
            // string; split out the level for the badge.
            const labels = {
                payment_delay_risk: 'Payment',
                liquidated_damages_risk: 'Delay Penalty (LD)',
                scope_risk: 'Scope',
                quantity_risk: 'Quantity',
                escalation_risk: 'Price Escalation',
                geotechnical_risk: 'Geotechnical',
                design_liability_risk: 'Design',
                contractual_liability_risk: 'Contractual Liability',
                force_majeure_risk: 'Force Majeure',
                approval_risk: 'Approval',
                material_supply_risk: 'Material Supply',
                subcontractor_risk: 'Subcontractor',
                arbitration_risk: 'Dispute & Arbitration',
                performance_guarantee_risk: 'Performance Guarantee',
                termination_risk: 'Termination',
            };
            const r = this.riskAssessment || {};
            return Object.keys(labels).map(key => {
                const raw = (r[key] || '').trim();
                const m = raw.match(/^\s*(low|medium|high|unknown)\b\s*[-:–]?\s*(.*)$/i);
                const level = m ? m[1].toLowerCase() : (raw ? 'unknown' : '');
                const detail = m && m[2] ? m[2] : raw;
                return { key, label: labels[key], level, detail, raw };
            });
        },
        riskComputed() {
            // True once at least one risk dimension has a value.
            return this.riskRows.some(row => row.raw);
        },
        // ── Risk severity triage ────────────────────────────────────────────
        // Presentation only: these computeds re-order and emphasise the rows
        // produced by riskRows() — they never alter a risk's level or detail.
        // HIGH first, then MEDIUM, LOW, then unknown/blank. Array.prototype.sort is
        // stable in every supported engine, so the backend's RISK_KEYS order is
        // preserved inside each severity band. Sorts a copy, never riskRows itself.
        sortedRiskRows() {
            const rank = { high: 0, medium: 1, low: 2 };
            return this.riskRows
                .slice()
                .sort((a, b) => {
                    const ra = rank[a.level] === undefined ? 3 : rank[a.level];
                    const rb = rank[b.level] === undefined ? 3 : rank[b.level];
                    return ra - rb;
                });
        },
        riskCounts() {
            const counts = { high: 0, medium: 0, low: 0, other: 0, total: 0 };
            this.riskRows.forEach(row => {
                counts.total += 1;
                if (counts[row.level] === undefined) counts.other += 1;
                else counts[row.level] += 1;
            });
            return counts;
        },
        // The demoted band: LOW plus anything whose level could not be parsed.
        lowRiskRows() {
            return this.sortedRiskRows.filter(row => row.level === 'low' || !['high', 'medium'].includes(row.level));
        },
        lowRiskLabel() {
            const n = this.lowRiskRows.length;
            // "low risks" when the band is purely LOW; "lower-severity risks" once
            // unknown/blank rows are folded in, so the label never misstates a level.
            const pure = this.lowRiskRows.every(row => row.level === 'low');
            if (pure) return n === 1 ? 'low risk' : 'low risks';
            return n === 1 ? 'lower-severity risk' : 'lower-severity risks';
        },
        // What actually renders. An active filter chip wins over the
        // collapse-low default; with no filter, LOW/unknown show only when expanded.
        visibleRiskRows() {
            if (this.riskFilter !== 'all') {
                if (this.riskFilter === 'low') return this.lowRiskRows.filter(row => row.level === 'low');
                return this.sortedRiskRows.filter(row => row.level === this.riskFilter);
            }
            if (this.showLowRisks) return this.sortedRiskRows;
            return this.sortedRiskRows.filter(row => row.level === 'high' || row.level === 'medium');
        },
        // First few qualifying requirements across both groups, for the
        // Eligibility card's preview. Technical first, then Financial, tagged
        // so the reader can tell which group a row came from.
        eligibilityPreview() {
            const take = (rows, group) => (rows || [])
                .filter(r => r && r.term_condition)
                .map(r => ({
                    id: `${group}-${r.id != null ? r.id : r.term_condition}`,
                    group,
                    clause: r.clause_no,
                    text: r.term_condition,
                }));
            return [...take(this.eligibilityTechnicalList, 'Technical'),
                    ...take(this.eligibilityFinancialList, 'Financial')].slice(0, 3);
        },
        // ── Analysis portlet states ─────────────────────────────────────────
        // 'ready' (has data) | 'processing' (analysing, nothing yet) | 'empty'.
        // Keys match the portlet blocks in the Analysis tab.
        analysisCardStates() {
            const has = {
                summary: !!(this.summaryDataList && this.summaryDataList.length),
                riskregister: !!(this.riskRegisterList && this.riskRegisterList.length),
                acceptance: !!(this.acceptanceTableDataList && this.acceptanceTableDataList.length),
                obligations: !!(this.obligationSummary && this.obligationSummary.total),
                // Viewers can't fetch the preview (admin-only endpoint), so for
                // them the card reports ready once analysis is done rather than
                // claiming there are no criteria.
                eligibility: this.isCompanyAdmin
                    ? !!this.eligibilityPreview.length
                    : !this.analysing,
            };
            const out = {};
            Object.keys(has).forEach(k => {
                out[k] = has[k] ? 'ready' : (this.analysing ? 'processing' : 'empty');
            });
            return out;
        },
    },
    watch: {
        // `mode` is derived from the bid status, which arrives EITHER from the
        // ?status= query param (instant) OR from the async fetchDetail() — so at
        // mount time it can still be the 'pre' default even for a post-bid
        // project (e.g. opened by refresh/direct link, where no query param is
        // present). Gating the post-bid fetches on mode inside mounted() then
        // skipped them permanently and every post-bid portlet rendered empty.
        // Watching mode instead fires them the moment the real status is known,
        // whichever way it arrives. `immediate` covers the query-param case.
        mode: {
            immediate: true,
            handler(newMode) {
                if (newMode === 'post' && POST_BID_ENABLED) this.loadPostBidPortlets();
            },
        },
    },
    mounted() {
        this.fetchDetail();
        this.populateSummaryView();
        this.populateRiskRegisterView();
        this.fetchObligationSummary();
        this.populateCriteriaData();
        this.populateEligibilityData();
        this.fetchChecklistProgress();
        this.fetchRiskAssessment();
        // Addenda trail is admin-only and relevant in both modes.
        if (this.isCompanyAdmin) this.fetchAddenda();
    },
    methods: {
        // ── Risk severity triage ────────────────────────────────────────────
        // Clicking the active chip toggles back to All, so the grid is never
        // stuck hiding risks.
        setRiskFilter(level) {
            this.riskFilter = (this.riskFilter === level) ? 'all' : level;
        },
        // ── Analysis portlet states ─────────────────────────────────────────
        // `state-*` is the styling hook; `is-pending` mutes a card that has no
        // data to show yet. Neither is used to reorder the grid — see the note on
        // .bd-portlets-grid in the stylesheet for why the source order stands.
        analysisCardClass(key) {
            const state = this.analysisCardStates[key] || 'empty';
            return ['state-' + state, state === 'ready' ? 'is-ready' : 'is-pending'];
        },
        isAnalysisReady(key) {
            return this.analysisCardStates[key] === 'ready';
        },
        isAnalysisProcessing(key) {
            return this.analysisCardStates[key] === 'processing';
        },
        // Text that accompanies the state dot, so colour is never the only cue.
        analysisStateText(key) {
            const state = this.analysisCardStates[key] || 'empty';
            if (state === 'ready') return 'Ready';
            if (state === 'processing') return 'Analysing…';
            return 'No data';
        },
        authHeaders() {
            return { Authorization: 'Bearer ' + this.getAccessToken() };
        },
        // ── Mode-specific portlet loading ───────────────────────────────────
        // Called from the `mode` watcher rather than mounted(), so the data is
        // fetched as soon as the real bid status is known. The guards make these
        // idempotent: the watcher can fire more than once for the same bid.
        loadPostBidPortlets() {
            // Key the guard to the bid id: navigating from one bid's details to
            // another reuses this component, and those fetches must run again.
            const key = 'post:' + this.pdfId;
            if (this._loadedFor === key) return;
            this._loadedFor = key;
            this.fetchDeliverablesProgress();
            this.fetchProjectEmails();
            this.fetchContractDocs();
            this.fetchContacts();
            this.fetchObligationDocs();
            if (this.isCompanyAdmin) this.fetchEmailConfig();
        },
        // ── Addendum / Corrigendum upload (admin) ───────────────────────────
        fetchAddenda() {
            if (!this.pdfId) return;
            axios.get(`${this.$API_URL}/user/addenda?pdf_id=${this.pdfId}`, { headers: this.authHeaders() })
                .then(res => { this.addenda = (res.data && res.data.addenda) || []; })
                .catch(() => { /* non-fatal */ });
        },
        openAddendumModal() {
            this.addendumModal = { show: true, files: [], dragOver: false, error: '' };
        },
        closeAddendumModal() {
            if (this.uploadingAddendum) return;
            this.addendumModal.show = false;
        },
        triggerAddendumUpload() {
            this.$refs.addendumInput && this.$refs.addendumInput.click();
        },
        _addAddendumFiles(fileList) {
            const files = Array.from(fileList || []);
            const bad = files.find(f => !f.name.toLowerCase().endsWith('.pdf'));
            if (bad) { this.addendumModal.error = 'Only PDF files are allowed.'; return; }
            // De-duplicate by name+size against already-selected files.
            const existing = new Set(this.addendumModal.files.map(f => f.name + f.size));
            files.forEach(f => { if (!existing.has(f.name + f.size)) this.addendumModal.files.push(f); });
            this.addendumModal.error = '';
        },
        onAddendumSelected(event) {
            this._addAddendumFiles(event.target.files);
            event.target.value = '';   // allow re-selecting the same file later
        },
        onAddendumDrop(event) {
            this.addendumModal.dragOver = false;
            this._addAddendumFiles(event.dataTransfer && event.dataTransfer.files);
        },
        removeAddendumFile(i) {
            this.addendumModal.files.splice(i, 1);
        },
        formatFileSize(bytes) {
            if (!bytes && bytes !== 0) return '';
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        },
        async submitAddendum() {
            if (this.uploadingAddendum || !this.addendumModal.files.length) return;
            this.uploadingAddendum = true;
            this.addendumModal.error = '';
            try {
                const fd = new FormData();
                fd.append('pdf_id', this.pdfId);
                this.addendumModal.files.forEach(f => fd.append('uploaded_files', f));
                await axios.post(`${this.$API_URL}/user/upload-addendum`, fd, {
                    headers: this.authHeaders(),
                });
                this.addendumModal.show = false;
                notify.success('Addendum uploaded. Affected sections update in the background; refresh in a moment to see them.');
                // Refresh the trail shortly after (background job records it).
                setTimeout(() => this.fetchAddenda(), 3000);
            } catch (err) {
                console.error('Addendum upload failed', err);
                this.addendumModal.error = 'Upload failed: ' + (err.response?.data?.detail || err.message);
            } finally {
                this.uploadingAddendum = false;
            }
        },
        formatAddendumDate(iso) {
            if (!iso) return '';
            const d = new Date(iso);
            if (isNaN(d.getTime())) return iso;
            return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        },
        // ── Email Configuration (admin only) ────────────────────────
        async fetchEmailConfig() {
            if (!this.pdfId) return;
            try {
                const res = await axios.get(
                    `${this.$API_URL}/user/upload-pdf/email-config?pdf_id=${this.pdfId}`,
                    { headers: this.authHeaders() }
                );
                const data = res.data || {};
                this.emailConfig.configured = !!data.configured;
                if (data.configured) {
                    this.emailConfig.summary = {
                        contact_person_email: data.contact_person_email || '',
                        imap_server: data.imap_server || ''
                    };
                }
            } catch (e) {
                console.error('Failed to load email config', e);
            }
        },
        openEmailConfig() {
            // Open in a loading state, then fetch the saved config to pre-fill.
            // Empty form = first-time entry; otherwise fields are populated.
            this.emailConfig.show = true;
            this.emailConfig.loading = true;
            this.emailConfig.saving = false;
            this.emailConfig.showPassword = false;
            this.emailConfig.error = '';
            this.emailConfig.form = { contact_person_email: '', email_password: '', imap_server: '' };

            axios.get(`${this.$API_URL}/user/upload-pdf/email-config?pdf_id=${this.pdfId}`, {
                headers: this.authHeaders()
            }).then((res) => {
                const data = res.data || {};
                if (data.configured) {
                    this.emailConfig.form = {
                        contact_person_email: data.contact_person_email || '',
                        email_password: data.email_password || '',
                        imap_server: data.imap_server || ''
                    };
                }
            }).catch((error) => {
                if (error.response && error.response.status === 403) {
                    this.emailConfig.error = 'Only admins can view email configuration.';
                } else {
                    this.emailConfig.error = 'Couldn’t load the saved configuration. Close this and try again.';
                }
                console.error('Email config load failed', error);
            }).finally(() => {
                this.emailConfig.loading = false;
            });
        },
        closeEmailConfig() {
            this.emailConfig.show = false;
        },
        async saveEmailConfig() {
            const form = this.emailConfig.form;
            if (!form.contact_person_email || !form.email_password || !form.imap_server) {
                this.emailConfig.error = 'All three fields are required.';
                return;
            }
            // Proper email validation — requires a domain with a TLD, so "a@bcc"
            // (which the lenient HTML email input accepts) is rejected. Mirrors backend.
            const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
            if (!emailRegex.test(form.contact_person_email)) {
                this.emailConfig.error = 'Enter a valid email address, like name@company.com.';
                return;
            }
            this.emailConfig.error = '';
            this.emailConfig.saving = true;
            try {
                await axios.post(`${this.$API_URL}/user/upload-pdf/email-config`, {
                    pdf_id: Number(this.pdfId),
                    contact_person_email: form.contact_person_email,
                    email_password: form.email_password,
                    imap_server: form.imap_server
                }, { headers: this.authHeaders() });
                this.emailConfig.show = false;
                await this.fetchEmailConfig();
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    this.emailConfig.error = 'Only admins can update email configuration.';
                } else if (error.response && error.response.data && error.response.data.detail) {
                    const detail = error.response.data.detail;
                    if (typeof detail === 'string') {
                        this.emailConfig.error = detail;
                    } else if (Array.isArray(detail) && detail.length && detail[0].msg) {
                        this.emailConfig.error = detail[0].msg.replace(/^Value error,\s*/, '');
                    } else {
                        this.emailConfig.error = 'Couldn’t save. Check the values and try again.';
                    }
                } else {
                    this.emailConfig.error = 'Couldn’t save the email configuration. Try again.';
                }
                console.error('Email config save failed', error);
            } finally {
                this.emailConfig.saving = false;
            }
        },
        // ── Status changes (admin) ──────────────────────────────────────────
        // The offered actions come from utils/bidStatus.js; the backend rejects
        // disallowed transitions with a 400 and a reason we show as-is.
        statusActionClass(action) {
            if (action.primary) return 'is-primary';
            if (action.subtle) return 'is-subtle';
            return 'is-secondary';
        },
        async changeStatus(action) {
            if (this.changingStatus) return;
            if (action.confirm && !(await confirmAction(action.confirm))) return;
            this.changingStatus = action.to;
            try {
                await axios.post(
                    `${this.$API_URL}/user/start-project?pdf_id=${this.pdfId}&status=${action.to}`,
                    null,
                    { headers: this.authHeaders() }
                );
                this.bid = { ...this.bid, bid_status: action.to };
                // Drop the cached row so the listing and this page reload fresh.
                sessionStorage.removeItem('bid-detail-' + this.pdfId);
                // Keep ?status= in step so a reload before the row arrives agrees.
                this.$router.replace({ query: { ...this.$route.query, status: action.to } });
                notify.success(action.success);
            } catch (e) {
                const detail = e && e.response && e.response.data && e.response.data.detail;
                if (e && e.response && e.response.status === 400 && typeof detail === 'string') {
                    notify.error(detail);
                } else {
                    notify.error('Couldn’t change the bid status. Check your connection and try again.');
                }
            } finally {
                this.changingStatus = null;
            }
        },
        // ── Section tabs ────────────────────────────────────────────────────
        setTab(key) {
            if (key === this.activeTab) return;
            this.$router.replace({ query: { ...this.$route.query, tab: key } });
        },
        onTabKeydown(event) {
            const keys = this.tabs.map(t => t.key);
            const i = keys.indexOf(this.activeTab);
            let next = null;
            if (event.key === 'ArrowRight') next = keys[(i + 1) % keys.length];
            else if (event.key === 'ArrowLeft') next = keys[(i - 1 + keys.length) % keys.length];
            else if (event.key === 'Home') next = keys[0];
            else if (event.key === 'End') next = keys[keys.length - 1];
            if (!next) return;
            event.preventDefault();
            this.setTab(next);
            this.$nextTick(() => {
                const el = document.getElementById('bd-tab-' + next);
                if (el) el.focus();
            });
        },
        // ── Email records for this project (moved from CompanyAdmin) ──
        async fetchProjectEmails() {
            if (!this.pdfId) return;
            this.emailsLoading = true;
            try {
                const res = await axios.get(
                    `${this.$API_URL}/company_admin/mail-records?project_id=${this.pdfId}&page=1&page_size=50`,
                    { headers: this.authHeaders() }
                );
                this.emailRecords = res.data.items || [];
            } catch (e) {
                console.error('Failed to load email records', e);
                this.emailRecords = [];
            } finally {
                this.emailsLoading = false;
            }
        },
        async fetchContacts() {
            if (!this.pdfId) return;
            this.contactsLoading = true;
            try {
                const res = await axios.get(
                    `${this.$API_URL}/company_admin/client-contact-details?project_id=${this.pdfId}`,
                    { headers: this.authHeaders() }
                );
                this.contacts = res.data.items || [];
            } catch (e) {
                console.error('Failed to load contacts', e);
                this.contacts = [];
            } finally {
                this.contactsLoading = false;
            }
        },
        goToContactList() {
            this.$router.push({ name: 'ProjectBoard', params: { pdf_id: this.pdfId }, query: { tab: 'contact-list', from: this.sidebarActive } });
        },
        goToEmailRecords() {
            this.$router.push({ name: 'CompanyAdmin', query: { tab: 'emails', project_id: this.pdfId, from: this.sidebarActive } });
        },
        goToContractDocs() {
            // Pass the project id so the Admin Panel's Bids tab shows ONLY this
            // project's record and its documents (same scoping as goToObligations).
            // Without it the tab opened unfiltered and listed every project.
            this.$router.push({
                name: 'CompanyAdmin',
                query: { tab: 'pdfs', project_id: this.pdfId, from: this.sidebarActive },
            });
        },
        goToUploadPage() {
            this.$router.push({ name: 'ProjectBoard', params: { pdf_id: this.pdfId }, query: { tab: 'upload-mail', from: this.sidebarActive } });
        },
        goToObligations() {
            this.$router.push({ name: 'CompanyAdmin', query: { tab: 'obligations', project_id: this.pdfId, from: this.sidebarActive } });
        },
        generateObligationMail() {
            this.$router.push({ name: 'CompanyAdmin', query: { tab: 'obligations', pdf_id: this.pdfId, from: this.sidebarActive } });
        },
        async fetchContractDocs() {
            if (!this.pdfId) return;
            this.contractDocsLoading = true;
            try {
                const res = await axios.get(
                    `${this.$API_URL}/company_admin/mail-records?project_id=${this.pdfId}&page=1&page_size=100`,
                    { headers: this.authHeaders() }
                );
                this.contractDocs = (res.data.items || [])
                    .filter(r => r.has_file)
                    .map(r => ({
                        id: r.id,
                        name: r.filepath ? r.filepath.replace(/\\/g, '/').split('/').pop() : `File_${r.id}.pdf`,
                        date: r.email_date,
                        type: r.email_type,
                    }));
            } catch (e) {
                console.error('Failed to load contract documents', e);
                this.contractDocs = [];
            } finally {
                this.contractDocsLoading = false;
            }
        },
        async fetchObligationDocs() {
            if (!this.pdfId) return;
            this.obligationDocsLoading = true;
            try {
                const res = await axios.get(
                    `${this.$API_URL}/company_admin/performance-obligations-failures?pdf_id=${this.pdfId}`,
                    { headers: this.authHeaders() }
                );
                this.obligationDocs = (res.data || [])
                    .filter(r => r.pdf_letter_name)
                    .map(r => ({
                        id: r.id,
                        name: r.pdf_letter_name.replace(/\\/g, '/').split('/').pop(),
                        date: r.created_date,
                        obligation: r.obligation
                    }));
            } catch (e) {
                console.error(e);
                this.obligationDocs = [];
            } finally {
                this.obligationDocsLoading = false;
            }
        },
        viewEmailDetail(emailId) {
            this.emailDetail = { show: true, loading: true, data: null };
            this.$nextTick(() => { if (this.$refs.emailDetailClose) this.$refs.emailDetailClose.focus(); });
            axios.get(`${this.$API_URL}/company_admin/mail-records/${emailId}`, { headers: this.authHeaders() })
                .then(res => {
                    this.emailDetail = { show: true, loading: false, data: res.data || {} };
                })
                .catch(e => {
                    console.error(e);
                    this.emailDetail = { show: false, loading: false, data: null };
                    notify.error('Couldn’t load the email. Try again.');
                });
        },
        closeEmailDetail() {
            this.emailDetail = { show: false, loading: false, data: null };
        },
        async downloadMailPdf(mailPdfId) {
            if (this.downloadingEmailIds.includes(mailPdfId)) return;
            this.downloadingEmailIds.push(mailPdfId);
            try {
                const res = await axios.get(`${this.$API_URL}/company_admin/download-mail/${mailPdfId}`, {
                    headers: this.authHeaders(),
                    responseType: 'blob',
                });
                const contentType = res.headers['content-type'] || 'application/octet-stream';
                const isZip = contentType.includes('zip');
                let filename = isZip ? `mail_files_${mailPdfId}.zip` : `mail_pdf_${mailPdfId}.pdf`;
                const disp = res.headers['content-disposition'] || res.headers['Content-Disposition'];
                if (disp) {
                    const m = disp.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/i);
                    if (m && m[1]) filename = decodeURIComponent(m[1].replace(/["']/g, ''));
                }
                const blob = new Blob([res.data], { type: contentType });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (e) {
                console.error(e);
                notify.error('Couldn’t download the file. Try again.');
            } finally {
                this.downloadingEmailIds = this.downloadingEmailIds.filter(id => id !== mailPdfId);
            }
        },
        async downloadObligationPdf(obligationPdfId) {
            if (this.downloadingEmailIds.includes(obligationPdfId)) return;
            this.downloadingEmailIds.push(obligationPdfId);
            try {
                const res = await axios.get(`${this.$API_URL}/company_admin/download-obligation/${obligationPdfId}`, {
                    headers: this.authHeaders(),
                    responseType: 'blob'
                });
                const contentType = res.headers['content-type'] || res.headers['Content-Type'] || 'application/octet-stream';
                const isZip = contentType.includes('zip');
                let filename = isZip ? `obligation_files_${obligationPdfId}.zip` : `obligation_pdf_${obligationPdfId}.pdf`;
                const disp = res.headers['content-disposition'] || res.headers['Content-Disposition'];
                if (disp) {
                    const m = disp.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/i);
                    if (m && m[1]) filename = decodeURIComponent(m[1].replace(/["']/g, ''));
                }
                const blob = new Blob([res.data], { type: contentType });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (e) {
                console.error(e);
                notify.error('Couldn’t download the claim letter. Try again.');
            } finally {
                this.downloadingEmailIds = this.downloadingEmailIds.filter(id => id !== obligationPdfId);
            }
        },
        stripExt(name) {
            if (!name) return '';
            // Remove every .pdf (handles comma-joined multi-file names too)
            return name.replace(/\.pdf/gi, '').replace(/[-_]/g, ' ');
        },
        // ── Inline project-name editing ─────────────────────────────
        startEditName() {
            if (!this.isCompanyAdmin) return;   // viewers cannot rename
            // Seed the input with the readable name (without .pdf extension).
            this.editNameValue = this.stripExt(this.bid.project_name || this.bid.pdf_name || '');
            this.editingName = true;
            this.$nextTick(() => {
                const el = this.$refs.nameInput;
                if (el) { el.focus(); el.select(); }
            });
        },
        cancelEditName() {
            this.editingName = false;
            this.editNameValue = '';
        },
        async saveName() {
            const newName = (this.editNameValue || '').trim();
            if (!newName) { notify.error('Enter a project name.'); return; }
            if (newName === this.stripExt(this.bid.project_name || this.bid.pdf_name || '')) {
                this.cancelEditName();
                return;
            }
            this.savingName = true;
            try {
                const form = new FormData();
                form.append('project_name', newName);
                const res = await axios.post(
                    `${this.$API_URL}/user/update-project-name?pdf_id=${this.pdfId}`,
                    form,
                    { headers: this.authHeaders() }
                );
                const saved = (res.data && res.data.project_name) || newName;
                // project_name is the editable display name; pdf_name stays the
                // original file name. Sync both so every view reflects the rename.
                this.bid = { ...this.bid, project_name: saved };
                // Refresh the cached row so a back-and-forth shows the new name.
                try {
                    sessionStorage.setItem('bid-detail-' + this.pdfId, JSON.stringify(this.bid));
                } catch (e) { /* ignore storage errors */ }
                this.editingName = false;
                this.editNameValue = '';
            } catch (err) {
                console.error('Failed to update project name', err);
                const detail = err?.response?.data?.detail;
                notify.error(typeof detail === 'string' ? detail : 'Couldn’t rename the project. Try again.');
            } finally {
                this.savingName = false;
            }
        },
        formatDate(d) {
            if (!d) return '—';
            return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        },
        truncateWords(text, limit = 100) {
            if (!text) return '';
            const words = String(text).trim().split(/\s+/);
            if (words.length <= limit) return text;
            return words.slice(0, limit).join(' ') + '...';
        },
        async fetchDetail() {
            if (!this.pdfId) { this.loading = false; return; }
            const id = this.pdfId;

            // 1) The row handed over from the listing renders instantly...
            try {
                const cached = sessionStorage.getItem('bid-detail-' + id);
                if (cached) {
                    this.bid = { ...this.bid, ...JSON.parse(cached) };
                    this.loading = false;
                }
            } catch (e) { /* ignore parse/storage errors */ }

            // 2) ...then the server's row replaces it, so status, analysis state
            //    and tender link are never stale.
            try {
                const res = await axios.get(
                    this.$API_URL + `/user/get-pdf-deatil?pdf_id=${id}`,
                    { headers: this.authHeaders() }
                );
                if (String(this.pdfId) !== String(id)) return;
                if (res.data && res.data.id !== undefined) {
                    this.bid = { ...this.bid, ...res.data };
                    try {
                        sessionStorage.setItem('bid-detail-' + id, JSON.stringify(this.bid));
                    } catch (e) { /* ignore storage errors */ }
                }
            } catch (err) {
                console.error('Failed to load bid detail', err);
                if (!this.bid.id) notify.error('Couldn’t load this bid. Check your connection and refresh the page.');
            } finally {
                this.loading = false;
            }
        },
        goBack() {
            this.$router.go(-1);
        },

        populateSummaryView: function () {
            if (!this.pdfId) { this.loading = false; return; }
            const pdf_id = this.pdfId; // Get ID from route parameters
            if (!pdf_id) return; // Guard clause
            axios.get(this.$API_URL + '/user/pdf-summary-details?pdf_id=' + pdf_id + '&page=1&page_size=2', {
                headers: this.authHeaders()
            })
                .then(res => {
                    this.summaryDataList = res.data.items; // backend ka table JSON

                })
                .catch(err => console.error(err));
        },
        populateRiskRegisterView: function () {
            if (!this.pdfId) { return; }
            const pdf_id = this.pdfId;
            if (!pdf_id) return;
            axios.get(this.$API_URL + '/user/risk-register-details?pdf_id=' + pdf_id + '&page=1&page_size=2', {
                headers: this.authHeaders()
            })
                .then(res => {
                    this.riskRegisterList = res.data.items;
                })
                .catch(err => console.error(err));
        },
        populateCriteriaData: function () {
            if (!this.pdfId) { this.loading = false; return; }
            const pdf_id = this.pdfId; // Get ID from route parameters
            if (!pdf_id) return; // Guard clause

            axios.get(this.$API_URL + '/user/pdf-criteria-details?pdf_id=' + pdf_id + '&page=1&page_size=3', {
                headers: this.authHeaders()
            })
                .then(res => {
                    this.acceptanceTableDataList = res.data.items; // backend ka table JSON
                })
                .catch(err => console.error(err));
        },
        // Technical + Financial qualifying requirements, for the Eligibility
        // card's preview. The full list lives on the Eligibility page.
        populateEligibilityData: function () {
            // The endpoint is admin-only (require_roles("admin")), so a viewer
            // would just collect a 403 — they still get the Detail View button.
            if (!this.pdfId || !this.isCompanyAdmin) return;
            axios.get(this.$API_URL + '/company_admin/pdf-eligibility-details?pdf_id=' + this.pdfId, {
                headers: this.authHeaders()
            })
                .then(res => {
                    const items = (res.data && res.data.items) || {};
                    this.eligibilityTechnicalList = Array.isArray(items.Technical) ? items.Technical : [];
                    this.eligibilityFinancialList = Array.isArray(items.Financial) ? items.Financial : [];
                })
                .catch(err => console.error('Failed to load eligibility criteria', err));
        },
        viewSummary() {
            this.$router.push({
                name: 'PdfView',
                params: { pdf_id: this.pdfId },
                query: this.subPageQuery(),
            });
        },
        subPageQuery() {
            return { status: this.storedStatus, from: this.sidebarActive };
        },
        viewEligibility() {
            this.$router.push({ name: 'EligibilityCriteria', params: { pdf_id: this.pdfId }, query: this.subPageQuery() });
        },
        viewRiskRegister() {
            this.$router.push({ name: 'RiskRegisterView', params: { pdf_id: this.pdfId }, query: this.subPageQuery() });
        },
        viewObligations() {
            this.$router.push({ name: 'ObligationsView', params: { pdf_id: this.pdfId }, query: this.subPageQuery() });
        },
        fetchObligationSummary() {
            if (!this.pdfId) return;
            axios.get(this.$API_URL + '/user/obligations?pdf_id=' + this.pdfId, {
                headers: this.authHeaders()
            })
                .then(res => {
                    this.obligationSummary = {
                        total: res.data.total || 0,
                        contractor_count: res.data.contractor_count || 0,
                        client_count: res.data.client_count || 0,
                    };
                })
                .catch(err => console.error('Failed to load obligation summary', err));
        },
        // Colour the Severity of Risk badge by its rating, matching the Risk
        // Register detail page: Critical=red, High=orange, Medium=yellow, Low=green.
        severityClass(severity) {
            const s = String(severity || '').trim().toLowerCase();
            if (s === 'critical') return 'sev-critical';
            if (s === 'high') return 'sev-high';
            if (s === 'medium') return 'sev-medium';
            if (s === 'low') return 'sev-low';
            return '';
        },
        viewAcceptance() {
            this.$router.push({ name: 'AcceptanceCriteria', params: { pdf_id: this.pdfId }, query: this.subPageQuery() });
        },
        viewChecklist() {
            // Keep the sidebar on the section we came from (pre-bid Bids /
            // post-bid Active/Completed).
            this.$router.push({ name: 'BidChecklist', params: { pdf_id: this.pdfId }, query: { from: this.sidebarActive } });
        },
        viewDeliverables() {
            // Carry the originating sidebar section so the deliverables page
            // highlights the same menu (post-bid Active/Completed vs pre-bid Bids).
            this.$router.push({
                name: 'Deliverables',
                params: { pdf_id: this.pdfId },
                query: { status: this.storedStatus, from: this.sidebarActive },
            });
        },
        fetchChecklistProgress() {
            // The backend 404s this endpoint while the feature is off.
            if (!CHECKLIST_ENABLED || !this.pdfId) return;
            axios.get(this.$API_URL + '/user/pdf-checklist?pdf_id=' + this.pdfId, {
                headers: this.authHeaders()
            })
                .then(res => {
                    const data = res.data || {};
                    this.checklistItems = Array.isArray(data.check_items) ? data.check_items : [];
                })
                .catch(err => console.error('Failed to load checklist progress', err));
        },
        fetchDeliverablesProgress() {
            if (!this.pdfId) return;
            axios.get(this.$API_URL + '/user/deliverables?pdf_id=' + this.pdfId, {
                headers: this.authHeaders()
            })
                .then(res => {
                    const data = res.data || {};
                    this.deliverables = Array.isArray(data.items) ? data.items : [];
                })
                .catch(err => console.error('Failed to load deliverables progress', err));
        },
        fetchRiskAssessment() {
            if (!this.pdfId) { this.riskLoading = false; return; }
            this.riskLoading = true;
            axios.get(this.$API_URL + '/user/pdf-risk-assessment?pdf_id=' + this.pdfId, {
                headers: this.authHeaders()
            })
                .then(res => {
                    this.riskAssessment = res.data || {};
                })
                .catch(err => console.error('Failed to load risk assessment', err))
                .then(() => { this.riskLoading = false; });
        },
        async downloadSummary() {
            await this.downloadBlob(`/download/summary?pdf_id=${this.pdfId}`, 'summary', v => (this.downloadingSummary = v));
        },
        async downloadAcceptance() {
            await this.downloadBlob(`/download/criteria?pdf_id=${this.pdfId}`, 'acceptance-criteria', v => (this.downloadingAcceptance = v));
        },
        async downloadRiskRegister() {
            await this.downloadBlob(`/download/risk-register?pdf_id=${this.pdfId}`, 'risk-register', v => (this.downloadingRiskRegister = v));
        },
        async downloadPpt() {
            // Full bid overview deck: basics + mini summary + mini risk + obligations.
            // Moved here from the Obligations page so it lives on the bid header.
            await this.downloadBlob(
                `/user/download/bid-overview-ppt?pdf_id=${this.pdfId}`,
                'bid-overview', v => (this.downloadingPpt = v), 'pptx',
            );
        },
        async downloadBlob(path, fallbackBase, setBusy, ext = 'xlsx') {
            setBusy(true);
            try {
                const res = await axios.get(this.$API_URL + path, {
                    responseType: 'blob',
                    headers: this.authHeaders(),
                });
                let filename = `${fallbackBase}-${this.pdfId}.${ext}`;
                const disp = res.headers['content-disposition'] || res.headers['Content-Disposition'];
                if (disp) {
                    const m = disp.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/i);
                    if (m && m[1]) filename = decodeURIComponent(m[1].replace(/['"]/g, ''));
                }
                const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(blob, filename);
                } else {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 100);
                }
            } catch (err) {
                console.error('Download failed', err);
                notify.error('Download failed. Check your connection and try again.');
            } finally {
                setBusy(false);
            }
        },
    },
};
</script>

<style scoped>
.bd-wrapper {
    width: 100%;
    position: relative;
    z-index: 2;
    font-family: var(--font-sans);

    /* ── Elevation scale ──────────────────────────────────────────────────
       Three steps, all built from the same navy-tinted shadow colour
       (rgba(15,23,42,…)) so stacked cards read as one material rather than
       a pile of different greys. Two layers each: a tight 1–2px contact
       shadow that anchors the card to the page, plus a wider soft spread
       that gives it height. Borders stay, but lighten to #eef2f7 now that
       the shadow does the separating work.
         --bd-e1  resting content card (metric tiles, detail card, panels)
         --bd-e2  resting interactive card (Analysis portlets)
         --bd-e3  hovered interactive card                                */
    --bd-e1: 0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 6px rgba(15, 23, 42, 0.05);
    --bd-e2: 0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.06);
    --bd-e3: 0 2px 4px rgba(15, 23, 42, 0.06), 0 10px 24px rgba(15, 23, 42, 0.10);
    /* Hero sits above everything else, so it gets a deeper, warmer-spread
       shadow — the only element on the page allowed this much weight. */
    --bd-e-hero: 0 2px 4px rgba(0, 31, 63, 0.10), 0 14px 32px rgba(0, 31, 63, 0.18);
    --bd-card-border: #eef2f7;

    /* ── Card hue families ───────────────────────────────────────────────
       Six families, assigned by WHAT THE CARD IS, not to make cards look
       different from each other. The rule that governs this palette: two
       cards share a hue only when they are genuinely the same kind of
       thing, so related cards look related and unrelated ones don't.

         NAVY    identity & narrative — Bid Summary, Contract Documents
                 (brand navy; the "what this bid is" family)
         VIOLET  criteria & compliance — Acceptance Criteria, Eligibility
                 Criteria, Obligations (the "what we must satisfy" family)
         ROSE    risk & exposure — Risk Register, AI Risk Assessment
         TEAL    progress & delivery — Features Checklist, Deliverables
         SKY     correspondence — Email Records
         PLUM    people — Client Contacts, Client Liaison
         MOSS    money owed — Compensation Claims

       Each family is a pair: a very light -bg wash for the icon square and
       the accent bar, and a saturated -fg for the glyph itself in the same
       hue. Both halves are deliberately quiet — a tint plus a line icon,
       never a saturated block — because two signals on this page are
       reserved and must stay louder than any of these:
         #0071C5  primary action ("Detail View" buttons)
         amber / red  urgency and risk severity
       Every -fg is checked at 4.5:1+ on its own -bg, so the glyph stays
       legible for anyone who needs the contrast. */
    --fam-navy-bg: #e8eef7;
    --fam-navy-fg: #1e4976;   /* 7.9:1 on --fam-navy-bg */
    --fam-violet-bg: #efeafc;
    --fam-violet-fg: #5b3fa8;   /* 6.6:1 on --fam-violet-bg */
    --fam-rose-bg: #fdeaec;
    --fam-rose-fg: #9f2d3c;   /* 6.2:1 on --fam-rose-bg */
    --fam-teal-bg: #e3f3f1;
    --fam-teal-fg: #176058;   /* 6.4:1 on --fam-teal-bg */
    --fam-sky-bg: #e6f1fa;
    --fam-sky-fg: #1b5b80;   /* 6.4:1 on --fam-sky-bg */
    --fam-plum-bg: #f5eaf3;
    --fam-plum-fg: #7a3368;   /* 7.2:1 on --fam-plum-bg */
    --fam-moss-bg: #e9f2e7;
    --fam-moss-fg: #2f6130;   /* 6.4:1 on --fam-moss-bg */
}

/* ===== Top bar (Back) ===== */
.bd-top-bar {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 1rem;
}

/* ===== Bottom footer (Back) ===== */
.bd-footer {
    display: flex;
    justify-content: flex-start;
    margin-top: 2rem;
    padding-top: 0.75rem;
    border-top: 1px dashed #eef2f7;
}

.bd-back {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #475569;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 0.55rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-sans);
}

.bd-back:hover {
    background: #f1f5f9;
    transform: translateX(-3px);
}

/* ===== Loading ===== */
.bd-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 5rem 0;
    color: #64748b;
}

.bd-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e2e8f0;
    border-top-color: #0071C5;
    border-radius: 999px;
    animation: spin 1s linear infinite;
}

/* ===== Hero ===== */
.bd-hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1.5rem;
    flex-wrap: wrap;
    /* Dark navy hero — a deliberate anchor at the top of a light page. It is
       the one place on this page where brand navy (#001F3F) is spent at full
       strength, so the eye lands on the bid identity before anything else.
       Not a flat slab: a diagonal gradient lifts the top-left corner toward
       #0a2f55 and settles the bottom-right at #00142b, and the ::after glow
       below adds a soft highlight so the surface reads as lit rather than
       painted. Every child is restyled for a dark ground; contrast figures
       are noted on each rule. */
    background:
        linear-gradient(135deg, #0a3360 0%, #001F3F 52%, #00132a 100%);
    color: #f8fafc;
    border: 1px solid #0a3360;
    border-radius: 10px;
    padding: 1.75rem 2rem;
    margin-bottom: 2rem;
    position: relative;
    overflow: hidden;
    box-shadow: var(--bd-e-hero);
}

/* Soft off-centre highlight: gives the navy surface a sense of being lit from
   the upper left instead of being one dead fill. Purely decorative and
   non-interactive, and it sits under the content (z-index 2 on both columns). */
.bd-hero::after {
    content: "";
    position: absolute;
    top: -55%;
    left: -10%;
    width: 60%;
    height: 180%;
    background: radial-gradient(ellipse at center,
            rgba(96, 165, 250, 0.16) 0%,
            rgba(96, 165, 250, 0) 68%);
    pointer-events: none;
    z-index: 0;
}

/* Hairline along the very top edge — catches the "light source" and keeps the
   hero from butting flatly against the page. */
.bd-hero::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 1px;
    background: linear-gradient(90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.22) 38%,
            rgba(255, 255, 255, 0) 100%);
    pointer-events: none;
    z-index: 1;
}

.bd-hero-left {
    position: relative;
    z-index: 2;
    flex: 1 1 0;
    min-width: 0;
    text-align: left;
}

.bd-category-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    padding: 0.35rem 0.85rem;
    border-radius: 999px;
    margin-bottom: 0.85rem;
    background: #f1f5f9;
    color: #334155;
}

.cat-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #7c93ad;
}

.cat-railway .cat-dot {
    background: #b08556;
}

.cat-highway .cat-dot {
    background: #5f9385;
}

.cat-metro .cat-dot {
    background: #8d84b3;
}

/* Status badge sitting at the top of the hero-left (where the category chip used to be) */
.hero-left-badge {
    margin-bottom: 0.85rem;
}

/* Category value in the Bid Information list — dot + colour-coded text */
.f-value.f-category {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 700;
}

.f-value.f-category.cat-railway {
    color: #86653f;
}

.f-value.f-category.cat-highway {
    color: #477065;
}

.f-value.f-category.cat-metro {
    color: #6b6291;
}

.f-value.f-category.cat-default {
    color: #55708c;
}

/* Status value: label pill + plain-language hint */
.f-value.f-status {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}

.f-hint {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-muted, #64748b);
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.bd-name {
    font-family: var(--font-display);   /* Outfit — bid page title */
    /* !important needed to beat the global .bd-name rule in overrides.css */
    font-size: 28px !important;         /* type scale: hero title */
    line-height: 1.25 !important;
    font-weight: 700 !important;
    margin: 0;
    letter-spacing: -0.02em;
    cursor: default;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
    /* On the navy hero: #ffffff on the #001F3F mid-stop = 16.6:1, and 12.7:1
       even over the lightest #0a3360 corner. Far past the 3:1 that 28px/700
       large text needs. */
    color: #ffffff !important;
}

.bd-name-editable {
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.15s ease, box-shadow 0.15s ease;
}

/* Rename affordance on the dark hero: a translucent white wash, not a grey one. */
.bd-name-editable:hover {
    background: rgba(255, 255, 255, 0.10);
    box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.10);
}

/* Inline edit row for the project name */
.bd-name-edit {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.bd-name-input {
    flex: 1 1 auto;
    min-width: 0;
    font-family: var(--font-display);
    font-size: 28px;
    line-height: 1.25;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #0f172a;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 0.25rem 0.6rem;
    outline: none;
}

.bd-name-input:focus {
    border-color: #0071C5;
    background: #ffffff;
}

.bd-name-input::placeholder {
    color: #94a3b8;
}

.bd-name-btn {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    color: #fff;
    transition: transform 0.12s ease, opacity 0.12s ease;
}

.bd-name-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.bd-name-btn.save {
    background: linear-gradient(90deg, #16a34a, #22c55e);
}

.bd-name-btn.cancel {
    background: #e2e8f0;
    color: #334155;      /* 8.5:1 on #e2e8f0 — the icon was white on dark before */
}

.bd-name-btn:not(:disabled):hover {
    transform: translateY(-1px);
}

.bd-subname {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin: 0.6rem 0 0;
    font-size: 0.85rem;
    font-family: var(--font-mono);
    /* Secondary text on the navy hero. #bfd3e8 on #001F3F = 10.8:1, and 8.3:1
       over the lightest #0a3360 corner — comfortably past the 4.5:1 that this
       0.85rem body text needs. A flat hex, not an opacity on white, so the
       figure holds wherever it lands on the gradient. */
    color: #bfd3e8 !important;
}

.bd-hero-right {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.85rem;
    flex-shrink: 0;
}

.bd-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 0.4rem 0.95rem;
    border-radius: 999px;
    /* On the navy hero: a translucent white chip with a hairline ring, so it
       reads as frosted glass on the navy rather than a pasted light block.
       #ffffff over the composited chip = 11.0:1 on the navy mid-stop and
       8.4:1 over the lightest corner. */
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.24);
    color: #ffffff;
}

/* The .sm variant is used inside the Bid Information list, on white — so it
   resets the hero's translucent treatment back to an opaque light chip. The
   st-* modifiers below then supply its own meaning-bearing colour. */
.bd-status-badge.sm {
    font-size: 0.7rem;
    padding: 0.2rem 0.7rem;
    background: #f1f5f9;
    border-color: transparent;
    color: #334155;      /* 9.5:1 on #f1f5f9 */
}

.bd-status-badge.sm.st-new {
    background: #e3f2fd;
    color: #005a9e;
}

.bd-status-badge.sm.st-expired {
    background: #fef3f2;
    color: #b42318;
}

.bd-status-badge.sm.st-win {
    background: #ecfdf3;
    color: #047857;
}

.bd-status-badge.sm.st-completed {
    background: #fffbeb;
    color: #92400e;
}

/* Countdown is a small pill, not a stacked block: amber when the deadline is
   close (urgent/soon from deadlineUrgency), quiet grey otherwise. */
.bd-countdown {
    display: inline-flex;
    flex-direction: row;
    align-items: baseline;
    gap: 0.35rem;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.22);
    padding: 0.3rem 0.8rem;
    border-radius: 999px;
    color: #ffffff;
}

.cd-num {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
}

.cd-cap {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    line-height: 1.2;
}

/* ≤3 days and ≤10 days both read as "deadline is close" → amber. Kept as a
   solid amber chip so urgency still punches off the navy instead of blending
   into the translucent default. #92400e on #fde68a = 5.7:1. */
.bd-countdown.urgent,
.bd-countdown.soon {
    background: #fde68a;
    border-color: #fcd34d;
    color: #92400e;
}

/* Not urgent → stays the quiet frosted chip, so amber keeps its signal value.
   #e2ecf7 over the composited chip = 7.5:1. */
.bd-countdown.normal,
.bd-countdown.closed {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.20);
    color: #e2ecf7;
}

/* Status actions (admin): primary filled, secondary outlined, subtle as text.
   They sit on the light hero, so outlines and text are navy/slate. */
.bd-status-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
}

.bd-status-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.6rem 1.2rem;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.2px;
    cursor: pointer;
    font-family: var(--font-sans);
    transition: background 0.2s ease, border-color 0.2s ease;
}

/* The one solid button in the hero: a light fill is the strongest contrast
   move available on navy, and it keeps #0071C5 free for the primary "Detail
   View" actions in the content below. #0a2440 on #ffffff = 15.7:1. */
.bd-status-btn.is-primary {
    background: #ffffff;
    border: 1px solid #ffffff;
    color: #0a2440;
}

.bd-status-btn.is-primary:hover:not(:disabled) {
    background: #dbe7f5;
    border-color: #dbe7f5;
}

/* Outlined — same frosted treatment as .bd-ppt-btn so the hero's action row
   reads as one set. #ffffff on the composited button = 12.5:1 on the navy
   mid-stop, 9.5:1 over the lightest corner. */
.bd-status-btn.is-secondary {
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.30);
    color: #ffffff;
}

.bd-status-btn.is-secondary:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.20);
    border-color: rgba(255, 255, 255, 0.46);
}

/* Text-only tertiary. #cbdcef on #001F3F = 11.9:1, and 9.1:1 over the
   lightest gradient corner. */
.bd-status-btn.is-subtle {
    background: transparent;
    border: 1px solid transparent;
    color: #cbdcef;
    padding-left: 0.6rem;
    padding-right: 0.6rem;
    text-decoration: underline;
    text-underline-offset: 3px;
}

.bd-status-btn.is-subtle:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
}

.bd-status-btn:disabled {
    opacity: 0.6;
    cursor: default;
}

/* Focus ring on the navy hero: #0071C5 would nearly vanish against the navy,
   so the ring goes white here. 16.6:1 against #001F3F. */
.bd-status-btn:focus-visible,
.bd-ppt-btn:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
}

.bd-addendum-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.6rem 1.2rem;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.2px;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
}

.bd-addendum-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.22);
    transform: translateY(-1px);
}

.bd-addendum-btn:disabled {
    opacity: 0.6;
    cursor: default;
}

/* Download PPT / View Tender — frosted outline buttons on the navy hero.
   Deliberately NOT filled with #0071C5: that blue is reserved for the
   "Detail View" primary actions further down the page. #ffffff text on the
   composited button = 11.7:1 on the navy mid-stop, 9.0:1 over the
   lightest corner. */
.bd-ppt-btn {
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.6rem 1.2rem;
    border: 1px solid rgba(255, 255, 255, 0.30);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;         /* icon inherits via currentColor */
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.2px;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.bd-ppt-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.22);
    border-color: rgba(255, 255, 255, 0.46);
    transform: translateY(-1px);
}

.bd-ppt-btn:disabled {
    opacity: 0.6;
    cursor: default;
}

.bd-analysis-note {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    margin: 0 0 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 10px;
    font-size: 0.85rem;
    line-height: 1.5;
    background: #e0f2fe;
    color: #075985;
}

.bd-analysis-note svg {
    flex-shrink: 0;
    margin-top: 0.15rem;
}

.bd-analysis-note.failed {
    background: #fef3f2;
    color: #b42318;
}

.bd-addenda-trail {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.5rem 0.85rem;
    border-radius: 10px;
    background: #f1f5f9;
    color: #475569;
    font-size: 0.8rem;
    font-weight: 600;
}

.bd-addenda-trail svg {
    flex-shrink: 0;
    color: #64748b;
}

/* ===== Embedded mail automation + email records ===== */
.bd-mail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
    margin-top: 0;
    margin-bottom: 1rem;
    align-items: start;
}

/* Cap height of the mail-automation portlet content so cards stay tidy and
   the action buttons line up across the grid. */
.bd-portlets-grid .portlet .summary-preview {
    max-height: 220px;
    overflow-y: auto;
}

/* Contract Documents folder tree — scrolls when both folders are open.
   Capped to the same height as the other previews so one card doesn't stretch
   the whole row and leave large gaps in the shorter portlets. */
.bd-portlets-grid .portlet .summary-preview.docs-tree {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 220px;
    overflow-y: auto;
    padding-right: 0.35rem;
}

.docs-tree::-webkit-scrollbar {
    width: 6px;
}

.docs-tree::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.bd-email-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    max-height: 150px;
    overflow-y: auto;
}

.bd-email-item {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.65rem 0.4rem;
    border-bottom: 1px solid #f1f5f9;
}

.bd-email-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    text-align: left;
}

.bd-email-subject {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bd-email-meta {
    font-size: 0.72rem;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bd-email-type {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #0369a1;
    background: #e0f2fe;
    padding: 3px 8px;
    border-radius: 999px;
    flex-shrink: 0;
}

.bd-email-item.bd-email-more {
    justify-content: center;
    padding: 0.5rem;
    color: #475467;
    font-size: 0.78rem;
}

.bd-more-link {
    background: none;
    border: none;
    font-family: var(--font-sans);
    color: #0071C5;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    border-radius: 6px;
}

.bd-more-link:hover {
    text-decoration: underline;
}

.bd-email-detail {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.4rem 1rem;
    margin: 0 0 1rem;
    font-size: 0.88rem;
}

.bd-email-detail dt {
    font-weight: 600;
    color: #475467;
}

.bd-email-detail dd {
    margin: 0;
    color: #0b1220;
    word-break: break-word;
}

.bd-email-body {
    max-height: 45vh;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
    padding: 0.85rem 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
    color: #1f2937;
    font-size: 0.85rem;
    line-height: 1.55;
}

.bd-email-actions {
    display: flex;
    gap: 0.35rem;
    flex-shrink: 0;
}

.bd-email-btn {
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #475569;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.bd-email-btn:hover:not(:disabled) {
    background: #f0f7ff;
    color: #0071C5;
    border-color: rgba(0, 113, 197, 0.3);
}

.bd-email-btn:disabled {
    opacity: 0.5;
    cursor: default;
}

@media (max-width: 900px) {
    .bd-mail-grid {
        grid-template-columns: 1fr;
    }
}

.bd-start-spin {
    animation: spin 1s linear infinite;
}

/* ===== Metric tiles ===== */
.bd-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;
}

.metric-tile {
    background: #fff;
    border: 1px solid var(--bd-card-border);
    border-radius: 10px;
    padding: 1.15rem 1.35rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: var(--bd-e1);
}

/* Static tile: resting elevation only, no hover lift — tiles are content,
   not controls, and lifting them would promise an interaction that isn't
   there. */

.metric-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

/* Metric tile icons draw from the same family palette, assigned by what the
   number means — not one hue per tile for variety's sake.

   MOSS — Estimated Value is money, same family as Compensation Claims. */
.metric-tile.blue .metric-icon {
    background: var(--fam-moss-bg);
    color: var(--fam-moss-fg);
}

/* The two date tiles are the same kind of fact — a milestone on the
   calendar — so they share NAVY rather than being split rose/green as they
   were before. Urgency against those dates is carried by the hero countdown
   pill, which is the one element allowed to go amber. */
.metric-tile.rose .metric-icon,
.metric-tile.green .metric-icon {
    background: var(--fam-navy-bg);
    color: var(--fam-navy-fg);
}

/* VIOLET — Category is a classification, the same kind of thing as the
   criteria family. All four category variants take one hue: the category's
   own identity is already carried by its label text and by the colour-coded
   .f-value.f-category in the Bid Information list, so tinting the icon
   square four different ways would be colour without added meaning. */
.metric-tile.purple .metric-icon,
.metric-tile.cat-railway .metric-icon,
.metric-tile.cat-highway .metric-icon,
.metric-tile.cat-metro .metric-icon,
.metric-tile.cat-default .metric-icon {
    background: var(--fam-violet-bg);
    color: var(--fam-violet-fg);
}

.metric-body {
    min-width: 0;
}

/* Type scale — field label (same treatment as .f-label) */
.metric-body label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
}

.metric-body h3 {
    margin: 0.2rem 0 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ===== Detail + portlets ===== */
/* Detail card on top (full width), portlets grid below it. */
.bd-detail-block {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.bd-detail-card {
    background: #fff;
    border: 1px solid var(--bd-card-border);
    border-radius: 10px;
    padding: 1.5rem 1.75rem;
    box-shadow: var(--bd-e1);
}

.bd-card-head {
    text-align: left;
}

/* Type scale — section heading ("Bid Information") */
.bd-card-head h3 {
    text-align: left;
    margin: 0 0 0.75rem;
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
}

.bd-field-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

/* Label column then value column. A flex row with space-between pushed the
   label to the far left and the value to the far right of a 1600px card,
   leaving a gulf the eye had to jump — the pair reads as one fact, so the
   value sits next to its label and the row stops at a readable measure. */
/* Label left, value right, hairline between — the value lands on a common
   right edge so a column of dates and amounts reads down the page. */
.bd-field-list li {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 2rem;
    padding: 0.8rem 0;
    border-bottom: 1px solid #f1f5f9;
}

.bd-field-list li > .f-label {
    flex-shrink: 0;
}

.bd-field-list li > .f-value {
    text-align: right;
    max-width: 70ch;
}

/* The long "Name of Work" paragraph stacks under its label instead. */
.bd-field-list li.fl-block {
    display: block;
}

.bd-field-list li:last-child {
    border-bottom: none;
}

/* Type scale — field label. Labels stay uppercase; values do not. */
.f-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    flex-shrink: 0;
}

/* Type scale — body */
.f-value {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.6;
    color: #1e293b;
    text-align: left;
}

.f-value.strong {
    font-weight: 700;
    color: #0071C5;
}

/* Dates / currency / counts align in a column */
.f-value.f-num,
.metric-body h3 {
    font-variant-numeric: tabular-nums;
}

/* Uploaded file name: monospace so the original filename reads literally */
.f-value.f-mono {
    font-family: var(--font-mono, ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace);
    font-size: 13px;
    word-break: break-all;
}

.f-link {
    color: #0071C5;
    font-weight: 600;
    text-decoration: none;
}

.f-link:hover {
    text-decoration: underline;
}

/* "Name of Work" is raw tender text, shown in full at a ~70ch measure.
   The characters are rendered exactly as the tender wrote them — NHAI, IREPS,
   EPC, BOQ and the rest are domain vocabulary, and no heuristic can separate
   an acronym from an ordinary word in an all-caps source without a lexicon
   that would rot. So all-caps is made readable typographically instead:
   a wider measure, open leading, a touch of positive tracking (caps need more
   than lowercase), and small-caps to drop the visual mass to x-height. */
/* "Name of Work" is the headline fact, so it sits on the same label-left /
   value-right line as every other row, but set larger and bolder. It is never
   clamped: a bid manager needs the whole scope statement, so it wraps onto as
   many lines as it needs and each line stays flush to the right edge. */
.bd-field-list li.fl-block {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 2rem;
}

.bd-field-list li.fl-block .f-label {
    flex-shrink: 0;
    margin-bottom: 0;
}

/* Selector carries the list ancestor so it outranks the row rule above, which
   right-aligns every other value. The block sits on the right with them, but
   its own lines read left-aligned — ragged-right, so the paragraph's left edge
   stays straight instead of each line starting somewhere different. */
.bd-field-list li > .f-value.f-prose {
    margin: 0;
    padding-left: 0;
    max-width: 70ch;
    text-align: left;
    line-height: 1.5;
    /* The headline fact of the card: set as a heading, not body copy. */
    font-size: 15px !important;
    font-weight: 600 !important;
    color: #0f172a;
    /* Never re-case the value — neither up nor down. */
    text-transform: none;
    /* Caps sit tighter than lowercase at the same tracking; a little positive
       letter-spacing measurably opens them up. */
    letter-spacing: 0.01em;
    /* Deliberately NOT font-variant-caps: all-small-caps. Inter ships no smcp
       feature, so browsers synthesize it by scaling glyphs — thin, slightly
       distorted, and it shrinks the capitals of a mixed-case value too (plenty
       of tenders do send mixed case). That is a fake, not a graceful fallback,
       so the tracking above carries the readability work on its own. */
}



/* ===== Portlets ===== */
/* Even, symmetric grid so all portlets line up left/right with equal-height
   rows and their action buttons align along the bottom. */
.bd-portlets-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    align-items: stretch;
}

.bd-portlets-grid .full-span {
    grid-column: 1 / -1;
}

/* ===== Section tabs ===== */
/* At most three short tabs (Overview · Analysis · Project), so they fit at any
   width — no overflow-x, which drew a scrollbar across an empty strip. */
.bd-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin: 0 0 2rem;
    border-bottom: 1px solid #e2e8f0;
}

.bd-tab {
    position: relative;
    background: none;
    border: none;
    padding: 0.75rem 1.1rem;
    font-family: var(--font-sans);
    font-size: 0.92rem;
    font-weight: 600;
    color: #475467;
    cursor: pointer;
    white-space: nowrap;
    border-radius: 10px 10px 0 0;
    transition: color 0.15s ease, background 0.15s ease;
}

.bd-tab:hover {
    color: #0b1220;
    background: #f1f5f9;
}

.bd-tab.active {
    color: #0071C5;
    font-weight: 700;
}

.bd-tab.active::after {
    content: "";
    position: absolute;
    left: 0.75rem;
    right: 0.75rem;
    bottom: -1px;
    height: 3px;
    border-radius: 3px 3px 0 0;
    background: #0071C5;
}

.bd-tab:focus-visible,
.bd-tabpanel:focus-visible {
    outline: 2px solid #0071C5;
    outline-offset: 2px;
}

@media (max-width: 900px) {
    .bd-portlets-grid {
        grid-template-columns: 1fr;
    }
}

.portlet {
    background: #fff;
    border: 1px solid var(--bd-card-border);
    border-radius: 10px;
    padding: 1.5rem;
    position: relative;
    overflow: hidden;
    transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
    /* Flex column so .portlet-actions pin to the bottom and align across the row */
    display: flex;
    flex-direction: column;
    box-shadow: var(--bd-e2);
}

/* Only the grid portlets stretch to equal height; stacked portlets size to content. */
.bd-portlets-grid .portlet {
    height: 100%;
}

/* Portlets are clickable surfaces, so the page answers the pointer: the card
   rises a hair and its shadow deepens. 1px of travel, which is enough to read
   as a response without shifting the text under the cursor. */
.portlet:hover {
    border-color: #dde7f3;
    box-shadow: var(--bd-e3);
    transform: translateY(-1px);
}

/* Anyone who asked the OS for less motion gets the shadow change only. */
@media (prefers-reduced-motion: reduce) {
    .portlet {
        transition: border-color 0.25s ease, box-shadow 0.25s ease;
    }

    .portlet:hover {
        transform: none;
    }
}

.portlet::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    border-radius: 10px 0 0 10px;
}

/* Severity of Risk badge — same colour legend as the Risk Register detail page. */
.sev-badge {
    display: inline-block;
    padding: 0 0.45rem;
    border-radius: 6px;
    font-weight: 700;
    line-height: 1.5;
}

.sev-badge.sev-critical {
    background: #ff0000;
    color: #ffffff;
}

.sev-badge.sev-high {
    background: #ffc000;
    color: #1e293b;
}

.sev-badge.sev-medium {
    background: #ffff00;
    color: #1e293b;
}

.sev-badge.sev-low {
    background: #00b050;
    color: #ffffff;
}

/* ── Family assignment ───────────────────────────────────────────────────
   A card's accent bar and its icon always take the SAME family, so the two
   agree and the Analysis grid reads as a set of distinguishable tools. The
   bar uses the family -fg at low opacity rather than the -bg wash, so it
   stays visible as a 5px sliver without becoming a saturated stripe. */

/* ROSE — risk & exposure */
.portlet.riskregister::before {
    background: var(--fam-rose-fg);
    opacity: 0.55;
}

.portlet.riskregister .portlet-icon {
    background: var(--fam-rose-bg);
    color: var(--fam-rose-fg);
}

/* VIOLET — criteria & compliance: Obligations, Eligibility, Acceptance all
   answer "what must this bid satisfy", so they share one hue. */
.portlet.obligations::before,
.portlet.eligibility::before,
.portlet.acceptance::before {
    background: var(--fam-violet-fg);
    opacity: 0.55;
}

.portlet.obligations .portlet-icon,
.portlet.eligibility .portlet-icon,
.portlet.acceptance .portlet-icon {
    background: var(--fam-violet-bg);
    color: var(--fam-violet-fg);
}

/* TEAL — progress & delivery: Deliverables and the Features Checklist are
   both "how far along are we". Matches the existing teal progress bar. */
.portlet.deliverables::before,
.portlet.checklist::before {
    background: var(--fam-teal-fg);
    opacity: 0.55;
}

.portlet.deliverables .portlet-icon,
.portlet.checklist .portlet-icon {
    background: var(--fam-teal-bg);
    color: var(--fam-teal-fg);
}

/* MOSS — money owed. Its own family: Compensation Claims is the only card
   on the page about money flowing back to us, and it is NOT a delivery or
   compliance artefact, so it does not borrow teal or violet. */
.portlet.docs.claims::before {
    background: var(--fam-moss-fg);
    opacity: 0.55;
}

.portlet.docs.claims .portlet-icon {
    background: var(--fam-moss-bg);
    color: var(--fam-moss-fg);
}

/* PLUM — people. Client Liaison is the same kind of thing as Client
   Contacts, so it shares the hue rather than getting a novelty colour. */
.portlet.contact.liaison::before {
    background: var(--fam-plum-fg);
    opacity: 0.55;
}

.portlet.contact.liaison .portlet-icon {
    background: var(--fam-plum-bg);
    color: var(--fam-plum-fg);
}

/* Avatar initials are a filled circle, so they take the saturated family
   -fg with white on top. #ffffff on #7a3368 = 8.4:1. */
.portlet.contact.liaison .bd-contact-avatar {
    background: var(--fam-plum-fg);
}

/* NAVY — identity & narrative: Bid Summary is the "what this bid is" card,
   and Contract Documents (.portlet.docs, further down) is the same family —
   the source material the summary is drawn from. */
.portlet.summary::before {
    background: var(--fam-navy-fg);
    opacity: 0.55;
}


/* Mini progress bar in the checklist portlet */
.cl-mini-progress {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem 0;
}

.cl-mini-bar-wrap {
    width: 100%;
    height: 8px;
    background: #eef2f7;
    border-radius: 10px;
    overflow: hidden;
}

.cl-mini-bar {
    height: 100%;
    background: linear-gradient(90deg, #6d28d9, #8b5cf6);
    border-radius: 10px;
    transition: width 0.4s ease;
}

.cl-mini-bar.teal {
    background: #0f766e;
}

.cl-overdue {
    color: #b42318;
}

.cl-mini-label {
    font-size: 0.8rem;
    font-weight: 700;
    color: #475569;
}

/* ===== AI Risk Assessment panel ===== */
.bd-risk-panel {
    margin-top: 2rem;
    background: #fff;
    border: 1px solid var(--bd-card-border);
    border-radius: 10px;
    padding: 1.5rem 1.75rem;
    box-shadow: var(--bd-e1);
}

.risk-head {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    margin-bottom: 0.75rem;
}

.risk-head-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    /* ROSE — same family as the Risk Register portlet, because it is the
       same subject. Related cards look related. */
    background: var(--fam-rose-bg);
    color: var(--fam-rose-fg);
}

.risk-head-text {
    flex: 1;
    min-width: 0;
    text-align: left;
}

/* Type scale — section heading ("AI Risk Assessment") */
.risk-head-text h3 {
    text-align: left;
    margin: 0 0 0.25rem;
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
}

.risk-head-text p {
    text-align: left;
    margin: 0;
    font-size: 0.82rem;
    color: #64748b;
}

.risk-ai-chip {
    flex-shrink: 0;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #fff;
    background: linear-gradient(135deg, #8b5cf6, #6d28d9);
    padding: 0.25rem 0.7rem;
    border-radius: 999px;
}

.risk-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    padding: 2rem;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 600;
}

.risk-state.empty {
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
    background: #f8fafc;
    color: #64748b;
}

.risk-spinner {
    width: 24px;
    height: 24px;
    border: 3px solid #e2e8f0;
    border-top-color: #64748b;
    border-radius: 999px;
    animation: spin 1s linear infinite;
}

/* ===== Risk severity triage ===== */
/* Counts + filter chips above the grid. Colours are the existing risk-level
   legend (rose / amber / emerald) — no new palette is introduced here. */
.risk-triage {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
}

.risk-triage-label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #64748b;
}

.risk-chips {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
}

.risk-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-family: var(--font-sans);
    font-size: 0.78rem;
    font-weight: 600;
    padding: 0.3rem 0.7rem;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.risk-chip .chip-count {
    font-weight: 800;
}

.risk-chip:hover {
    border-color: #cbd5e1;
}

.risk-chip:focus-visible {
    outline: 2px solid #0071C5;
    outline-offset: 2px;
}

.risk-chip.chip-high {
    color: #e11d48;
    border-color: rgba(244, 63, 94, 0.3);
    background: rgba(244, 63, 94, 0.07);
}

.risk-chip.chip-medium {
    color: #d97706;
    border-color: rgba(245, 158, 11, 0.3);
    background: rgba(245, 158, 11, 0.07);
}

.risk-chip.chip-low {
    color: #059669;
    border-color: rgba(16, 185, 129, 0.3);
    background: rgba(16, 185, 129, 0.07);
}

/* Active chip: same hue, filled — aria-pressed carries it for assistive tech. */
.risk-chip[aria-pressed="true"].chip-high {
    background: rgba(244, 63, 94, 0.18);
    border-color: #f43f5e;
}

.risk-chip[aria-pressed="true"].chip-medium {
    background: rgba(245, 158, 11, 0.18);
    border-color: #f59e0b;
}

.risk-chip[aria-pressed="true"].chip-low {
    background: rgba(16, 185, 129, 0.18);
    border-color: #10b981;
}

.risk-chip[aria-pressed="true"].chip-all {
    background: #e2e8f0;
    border-color: #94a3b8;
    color: #0f172a;
}

/* Disclosure for the demoted LOW / unknown band. */
.risk-lowtoggle {
    margin-top: 1rem;
}

.risk-low-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-family: var(--font-sans);
    font-size: 0.8rem;
    font-weight: 600;
    color: #475569;
    background: none;
    border: none;
    padding: 0.25rem 0;
    cursor: pointer;
}

.risk-low-btn:hover {
    color: #0f172a;
}

.risk-low-btn:focus-visible {
    outline: 2px solid #0071C5;
    outline-offset: 2px;
}

.risk-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
}

.risk-card {
    border: 1px solid #eef2f7;
    border-radius: 12px;
    padding: 1rem 1.1rem;
    background: #f8fafc;
    border-left: 4px solid #cbd5e1;
    transition: all 0.2s;
}

.risk-card:hover {
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
}

.risk-card.risk-high {
    border-left-color: #f43f5e;
    background: #fff1f2;
}

.risk-card.risk-medium {
    border-left-color: #f59e0b;
    background: #fffbeb;
}

.risk-card.risk-low {
    border-left-color: #10b981;
    background: #f0fdf4;
}

.risk-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}

.risk-name {
    font-size: 0.85rem;
    font-weight: 700;
    color: #0f172a;
}

.risk-badge {
    flex-shrink: 0;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    background: #e2e8f0;
    color: #64748b;
}

.risk-badge.lvl-high {
    background: rgba(244, 63, 94, 0.15);
    color: #e11d48;
}

.risk-badge.lvl-medium {
    background: rgba(245, 158, 11, 0.15);
    color: #d97706;
}

.risk-badge.lvl-low {
    background: rgba(16, 185, 129, 0.15);
    color: #059669;
}

.risk-detail {
    margin: 0;
    font-size: 0.78rem;
    line-height: 1.5;
    color: #475569;
    text-align: left;
}
/* HIGH risks read heavier than LOW: thicker severity rail + bolder title.
   Colours are unchanged — only the weight differs. */
.risk-card.risk-high {
    border-left-width: 6px;
}

.risk-card.risk-high .risk-name {
    font-weight: 800;
}

.risk-card.risk-medium {
    border-left-width: 4px;
}

.risk-card.risk-low,
.risk-card.risk-unknown,
.risk-card.risk-none {
    border-left-width: 3px;
}

/* ===== Analysis portlet state ===== */
/* Dot + text; the text is the real carrier of meaning, the dot is a shorthand. */
.portlet-state {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin-left: auto;
    flex-shrink: 0;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: #64748b;
    white-space: nowrap;
}

.portlet-state .ps-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #cbd5e1;
    flex-shrink: 0;
}

.portlet-state.ps-ready .ps-dot {
    background: #10b981;
}

.portlet-state.ps-ready {
    color: #059669;
}

.portlet-state.ps-processing .ps-dot {
    background: #f59e0b;
}

.portlet-state.ps-processing {
    color: #d97706;
}

.portlet-state.ps-empty .ps-dot {
    background: #cbd5e1;
}

/* PROCESSING / EMPTY cards sit back so READY cards carry the eye. */
.portlet.is-pending {
    background: #fbfcfe;
    /* A card with nothing in it yet also sits lower in the stack — it has not
       earned the full resting elevation of a ready card. */
    box-shadow: var(--bd-e1);
}

.portlet.is-pending .portlet-top h4,
.portlet.is-pending .portlet-top p {
    color: #64748b;
}

/* Not-ready cards keep their family hue — so you can still tell which tool
   you are looking at — but hold it at reduced strength, so "has content" vs
   "still waiting" reads across the grid without a second glance. The state
   dot and the muted heading say the same thing, so this is never
   colour-only information. */
.portlet.is-pending .portlet-icon {
    opacity: 0.5;
}

.portlet.is-pending::before {
    opacity: 0.25;
}

/* Visually hidden, still announced. */
.bd-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.portlet-quiet {
    margin: 0 0 1rem;
    font-size: 0.8rem;
    color: #94a3b8;
    text-align: left;
}

.ps-spin {
    color: #d97706;
}

/* NOTE: no ready-first visual reordering here, deliberately. Sorting the grid
   with CSS `order` would have made the visual order disagree with the DOM /
   tab order, so a keyboard or screen-reader user would see focus jump around
   the grid (WCAG 1.3.2 Meaningful Sequence). The portlets are five bespoke
   blocks that cannot be driven from one v-for without rewriting them all, so
   the grid keeps its source order and the per-card state dot + muted styling
   carry "what is ready" instead. */


@media (max-width: 1100px) {
    .risk-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 600px) {
    .risk-grid {
        grid-template-columns: 1fr;
    }
}

.portlet-top {
    display: flex;
    align-items: flex-start;
    gap: 0.9rem;
    margin-bottom: 0.75rem;
}

.portlet-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

/* NAVY — identity & narrative (see the family block above). */
.portlet.summary .portlet-icon {
    background: var(--fam-navy-bg);
    color: var(--fam-navy-fg);
}

/* SKY — correspondence. Mail automation and Email Records are the same
   kind of thing: the back-and-forth with the client. Sky is close to the
   #0071C5 action blue, so it is pulled well down in saturation and only
   ever appears as a wash behind a line icon — never as a fill on a
   control, which is what keeps it from reading as a button. */
.portlet.mail::before,
.portlet.email::before {
    background: var(--fam-sky-fg);
    opacity: 0.55;
}

.portlet.mail .portlet-icon,
.portlet.email .portlet-icon {
    background: var(--fam-sky-bg);
    color: var(--fam-sky-fg);
}

/* PLUM — people. */
.portlet.contact::before {
    background: var(--fam-plum-fg);
    opacity: 0.55;
}

.portlet.contact .portlet-icon {
    background: var(--fam-plum-bg);
    color: var(--fam-plum-fg);
}

/* NAVY — Contract Documents: the bid's own source material, same family as
   Bid Summary. Note .docs.claims above overrides this to MOSS, since a
   compensation claim is about money, not about the contract text. */
.portlet.docs::before {
    background: var(--fam-navy-fg);
    opacity: 0.55;
}

.portlet.docs .portlet-icon {
    background: var(--fam-navy-bg);
    color: var(--fam-navy-fg);
}

.bd-contact-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.55rem 0.25rem;
    border-bottom: 1px solid #f1f5f9;
}

.bd-contact-item:last-child { border-bottom: none; }

.bd-contact-avatar {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    background: #0369a1;
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.bd-contact-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    text-align: left;
}

.bd-contact-name {
    font-size: 0.85rem;
    font-weight: 700;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bd-contact-meta {
    font-size: 0.72rem;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bd-contact-more {
    display: block;
    width: 100%;
    background: none;
    border: none;
    text-align: center;
    font-family: var(--font-sans);
    font-size: 0.78rem;
    color: #0071C5;
    font-weight: 600;
    padding: 0.4rem;
    cursor: pointer;
    border-radius: 6px;
}

.bd-contact-more:hover {
    text-decoration: underline;
}

/* Shown in place of Admin Panel links for viewers (that page is admin-only). */
.admin-only-note {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 0.72rem 1rem;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px dashed #d0d5dd;
    color: #475467;
    font-size: 0.85rem;
    font-weight: 600;
}

/* ===== Folder & File List ===== */
.folder-block {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #f1f5f9;
}

.folder-header {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    width: 100%;
    padding: 0.6rem 1rem;
    background: #fff;
    border: none;
    text-align: left;
    font-family: var(--font-sans);
    cursor: pointer;
    user-select: none;
    transition: background 0.15s;
}

.folder-header:focus-visible,
.file-row-more:focus-visible,
.bd-more-link:focus-visible,
.bd-contact-more:focus-visible {
    outline: 2px solid #0071C5;
    outline-offset: -2px;
}

.folder-icon {
    color: #64748b;
    flex-shrink: 0;
}

.folder-chevron {
    margin-left: auto;
    color: #64748b;
    transition: transform 0.2s;
}

.folder-chevron.open {
    transform: rotate(90deg);
}

.file-icon {
    flex-shrink: 0;
}

.file-icon.pdf {
    color: #dc2626;
}

.file-icon.letter {
    color: #b45309;
}

.file-row.file-row-note {
    color: #64748b;
    font-size: 0.8rem;
    justify-content: center;
    padding: 0.75rem 1rem;
}

.file-row.file-row-more {
    width: 100%;
    background: none;
    border: none;
    font-family: var(--font-sans);
    color: #0071C5;
    font-size: 0.78rem;
    font-weight: 600;
    justify-content: center;
    padding: 0.5rem;
    cursor: pointer;
}

.file-row.file-row-more:hover {
    text-decoration: underline;
}

.folder-header:hover {
    background: #fef9ec;
}

.folder-name {
    font-size: 0.85rem;
    font-weight: 700;
    color: #374151;
}

.folder-count {
    font-size: 0.7rem;
    color: #64748b;
    background: #f1f5f9;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-weight: 600;
}

.file-list {
    display: flex;
    flex-direction: column;
    border-top: 1px solid #f1f5f9;
}

.file-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 1rem 0.5rem 2rem;
    border-bottom: 1px solid #f8fafc;
    transition: background 0.15s;
}

.file-row:last-child {
    border-bottom: none;
}

.file-row:hover {
    background: #f8fafc;
}

.file-name {
    font-size: 0.82rem;
    color: #374151;
    font-weight: 500;
}

.file-size {
    margin-left: auto;
    font-size: 0.72rem;
    color: #64748b;
    font-weight: 500;
    min-width: 55px;
    text-align: right;
}

.file-download-btn {
    background: none;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 0.25rem 0.4rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: #64748b;
    transition: all 0.15s;
}

.file-download-btn:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #2563eb;
}

.expand-enter-active,
.expand-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
}

.expand-enter-from,
.expand-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

/* Type scale — card title */
.portlet-top h4 {
    text-align: left;
    margin: 0 0 0.25rem;
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
}

.portlet-top p {
    text-align: left;
    margin: 0;
    font-size: 0.82rem;
    color: #64748b;
    line-height: 1.4;
}

/* ===== Summary preview ===== */
.summary-preview {
    margin-bottom: 0.75rem;
    border-top: 1px dashed #eef2f7;
    padding-top: 0.85rem;
    /* Fill available height so the action row aligns at the bottom of every
       portlet, keeping the buttons on a straight line across the grid. */
    flex: 1 1 auto;
}

.summary-items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 150px;
    overflow-y: auto;
    padding-right: 0.35rem;
}

.summary-items::-webkit-scrollbar {
    width: 5px;
}

.summary-items::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}

.summary-item {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 0.55rem 0.75rem;
    transition: all 0.2s;
}

.summary-item:hover {
    border-color: rgba(0, 132, 255, 0.3);
    background: #f0f9ff;
}

.si-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.82rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 0.25rem;
}

.si-bullet {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: linear-gradient(135deg, #0084FF, #0071C5);
    flex-shrink: 0;
}

.si-bullet.green {
    background: #94a3b8;
}

/* Acceptance-criteria card accent (green) */
.summary-item.green-accent:hover {
    border-color: #e2e8f0;
    background: #f8fafc;
}

.si-clause {
    margin-left: auto;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #475569;
    background: #f1f5f9;
    padding: 2px 8px;
    border-radius: 999px;
}

.si-desc {
    margin: 0;
    font-size: 0.78rem;
    line-height: 1.45;
    color: #475569;
    text-align: left;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.summary-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1.5rem;
    border: 1px dashed #d0d5dd;
    border-radius: 10px;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 600;
    background: #f8fafc;
}

.portlet-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: auto;
}

.pb-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.72rem 1rem;
    border-radius: 6px;
    font-weight: 700;
    font-size: 0.875rem;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: var(--font-sans);
    border: 1px solid transparent;
}

/* "Detail View" — confident outlined secondary button (was a flat grey blob).
   White surface + brand border/text so it reads clearly and pairs with the
   gradient primary buttons beside it. */
.pb-btn.detail {
    background: #0071C5;
    color: #ffffff;
    border: 1px solid #0071C5;
}

.pb-btn.detail svg {
    color: #ffffff;
}

.pb-btn.detail:hover {
    background: #005a9e;
    color: #ffffff;
    border-color: #005a9e;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.28);
    transform: translateY(-2px);
}

.pb-btn.detail:hover svg {
    color: #ffffff;
}

/* Download / secondary actions: one neutral outline treatment everywhere. */
.pb-btn.download,
.portlet.acceptance .pb-btn.download,
.pb-btn.download.deliverables-btn,
.portlet.contact.liaison .pb-btn.download {
    background: #ffffff;
    color: #334155;
    border: 1px solid #e2e8f0;
    box-shadow: none;
}

.pb-btn.download svg,
.portlet.acceptance .pb-btn.download svg,
.pb-btn.download.deliverables-btn svg,
.portlet.contact.liaison .pb-btn.download svg {
    color: #334155;
}

.pb-btn.download:hover,
.portlet.acceptance .pb-btn.download:hover,
.pb-btn.download.deliverables-btn:hover,
.portlet.contact.liaison .pb-btn.download:hover {
    background: #f8fafc;
    color: #334155;
    border-color: #e2e8f0;
}

.pb-btn:focus-visible {
    outline: 2px solid #0071C5;
    outline-offset: 2px;
}

.pb-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
}

.spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* ===== Responsive ===== */
@media (max-width: 1100px) {
    .bd-metrics {
        grid-template-columns: repeat(2, 1fr);
    }

    .bd-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 600px) {
    .bd-metrics {
        grid-template-columns: 1fr;
    }

    .bd-hero {
        flex-direction: column;
    }

    .bd-hero-right {
        align-items: flex-start;
    }

    .portlet-actions {
        flex-direction: column;
    }
}

/* ===== Email Config Modal ===== */
.ec-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(10, 15, 28, 0.55);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.ec-modal {
    width: 100%;
    max-width: 460px;
    background: #fff;
    border-radius: 10px;
    padding: 1.75rem;
    box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.35);
    animation: ecFadeIn 0.2s ease;
}

@keyframes ecFadeIn {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}

.ec-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.ec-modal-header h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
}

.ec-close {
    background: transparent;
    border: none;
    font-size: 1.6rem;
    line-height: 1;
    color: #64748b;
    cursor: pointer;
}

.ec-close:hover { color: #1e293b; }

.ec-subtitle {
    margin: 0.25rem 0 1.25rem;
    color: #64748b;
    font-size: 0.85rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ec-loading {
    padding: 2rem 0;
    text-align: center;
    color: #64748b;
    font-size: 0.9rem;
}

.ec-field { margin-bottom: 1rem; }

.ec-field label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #334155;
}

.ec-req { color: #ef4444; }

.ec-field input {
    width: 100%;
    padding: 0.6rem 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.9rem;
    color: #1e293b;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s;
}

.ec-field input:focus { border-color: #0ea5e9; }

.ec-password-row { display: flex; gap: 0.5rem; }
.ec-password-row input { flex: 1; }

.ec-toggle-pass {
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    border-radius: 6px;
    padding: 0 0.75rem;
    font-size: 0.8rem;
    cursor: pointer;
    white-space: nowrap;
}

.ec-toggle-pass:hover { background: #f1f5f9; }

.ec-error {
    color: #ef4444;
    font-size: 0.85rem;
    margin: 0 0 0.75rem;
}

/* ── Addendum upload modal ── */
.adm-help {
    font-size: 0.85rem;
    color: #64748b;
    line-height: 1.5;
    margin: 0 0 1rem;
}

.adm-drop {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    text-align: center;
    padding: 1.75rem 1rem;
    border: 2px dashed #cbd5e1;
    border-radius: 10px;
    background: #f8fafc;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;
}

.adm-drop:hover,
.adm-drop.is-drag {
    border-color: #7c3aed;
    background: #f5f3ff;
    color: #7c3aed;
}

.adm-drop p {
    margin: 0;
    font-size: 0.9rem;
}

.adm-drop .adm-hint {
    font-size: 0.75rem;
    color: #64748b;
}

.adm-file-list {
    list-style: none;
    margin: 0.9rem 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 220px;
    overflow-y: auto;
}

.adm-file-list li {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.55rem 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #fff;
}

.adm-file-list li svg {
    flex-shrink: 0;
    color: #7c3aed;
}

.adm-file-name {
    flex: 1 1 auto;
    font-size: 0.85rem;
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.adm-file-size {
    font-size: 0.75rem;
    color: #64748b;
    flex-shrink: 0;
}

.adm-file-remove {
    flex-shrink: 0;
    border: none;
    background: #fee2e2;
    color: #b91c1c;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
}

.adm-file-remove:disabled {
    opacity: 0.6;
    cursor: default;
}

.ec-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 0.5rem;
}

.ec-btn-cancel,
.ec-btn-save {
    padding: 0.55rem 1.25rem;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
}

.ec-btn-cancel { background: #f1f5f9; color: #475569; }
.ec-btn-cancel:hover:not(:disabled) { background: #e2e8f0; }

.ec-btn-save { background: #0369a1; color: #fff; }
.ec-btn-save:hover:not(:disabled) { background: #075985; }

.ec-btn-cancel:disabled,
.ec-btn-save:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
